// ════════════════════════════════════════════════════════════════════════════
// PRODUCT MODAL
// ════════════════════════════════════════════════════════════════════════════
//
// When a product card is clicked on the index, instead of navigating to
// /products/HANDLE we:
//   1. fetch the product page HTML
//   2. parse out the product UI (.product-page-wrapper + image-expand modal)
//   3. inject it into a full-viewport black overlay
//   4. load style-product.css temporarily so the product layout styles apply
//   5. re-execute the product page scripts so variants / expand / instruction-
//      manual / image zoom all work
//   6. update the URL via pushState — browser doesn't navigate, just displays
//      /products/HANDLE in the address bar; refresh = real page, back = close
//
// The modal uses `transform: translateZ(0)` so it acts as the containing block
// for any `position: fixed` descendants (style-product.css uses fixed on
// .product-page-scrollable and .product-description-container). Without that,
// those elements would anchor to the raw viewport and paint behind the modal.

const PRODUCT_MODAL = {
  overlayId: "claustrum-product-modal",
  styleId: "claustrum-product-modal-style",
  cssId: "claustrum-product-modal-css",
  readyEvent: "ClaustrumProductModalReady",
  cache: Object.create(null),
  closeTimer: null,        // pending close-finalize timeout, so a quick re-open can cancel it
  fadeInTimer: null,       // pending fadeIn cleanup timeout from a previous close
  blurOnTimer: null,       // pending "restore panel backdrop-filter after fade" timeout (see openProductSwap)
  fetchTimeoutMs: 30000,   // a slow fetch is loading, not failing — see pmFetchOk
  _footerNode: null,       // the footer node detached while the PDP is open (see pmRemoveFooter)
  _footerSlot: null,       // { parent, next } slot to restore the footer into on close
  _openSeq: 0,             // monotonic counter → unique ready-event name per open (see pmRunScripts)
  _htmlOverflowPrev: "",   // <html> overflow captured before the scroll-lock, restored on close
  _bodyOverflowPrev: "",   // <body> overflow captured before the lock (desktop path), restored on close
  _scrollLockY: 0,         // underlay scrollY captured at open; body is pinned at top:-Y, restored on close
  loaderId: "claustrum-product-modal-loader",
  loaderTimer: null,       // delayed-show timer for the cache-miss loading animation
};

// Fetch target toggle. Shopify serves the storefront product page from its
// internal full-page cache (Response header `ETag: page_cache:…ProductDetails
// Controller`, ~100ms processing) — but ONLY on the PUBLISHED theme, and only
// when the request matches the cached entry's key. The cache Varies on
// `Accept`, so a bare fetch (`Accept: */*`) misses; pmFetchOk now sends a
// navigation-style `Accept: text/html` to match.
//
//   true  → canonical /products/x. The bet for PRODUCTION: should land the
//           ~100ms page-cache hit a direct click-through gets. UNVERIFIED for
//           a fetch (cors) vs a document nav until tested on a published theme.
//   false → ?section_id=product-fragment slim fragment (layout none, product-body only). Always
//           cache-cold (distinct URL), but skips theme.liquid so it renders far
//           less Liquid.
//
// Currently FALSE because development happens on a PREVIEW theme, which Shopify
// never page-caches — so canonical's only upside doesn't exist here, and on the
// preview theme canonical renders the full heavy page (~1047ms, complexity
// ~1042) vs ?section_id=product-fragment (~295ms, complexity ~281). Flip to true and verify the
// page_cache ETag at publish time. See memory: pdp-modal-page-cache.
var PM_FETCH_CANONICAL = false;

// Product URL test that survives Shopify's locale prefix. This storefront serves
// an /en locale, where Liquid's product.url is "/en/products/x" — a bare
// startsWith("/products/") rejected every direct-link card, so none were ever
// prefetched or background-warmed and every click was a cold fetch.
function pmIsProductUrl(url) {
  return typeof url === "string" && /\/products\//.test(url);
}

function pmModalUrl(url) {
  if (PM_FETCH_CANONICAL) return url;
  return url + (url.indexOf("?") === -1 ? "?" : "&") + "section_id=product-fragment";
}

// Fetch with timeout that rejects on non-OK status. Storefront 403s
// (Shopify bot/rate-limit) used to be silently cached as "valid" HTML,
// poisoning every subsequent click on that product.
// `Accept: text/html` mimics a document navigation so the request matches
// Shopify's `Vary: Accept` page-cache key (a bare fetch sends `*/*` and misses).
function pmFetchOk(url) {
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), PRODUCT_MODAL.fetchTimeoutMs) : null;
  return fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8" },
    signal: ctrl ? ctrl.signal : undefined,
    // Fetch Priority: a click-driven product fetch must not queue behind the
    // grid's fetchpriority="high" images while the page is still loading.
    // Ignored by browsers that don't support it.
    priority: "high",
  })
    .then((r) => {
      if (t) clearTimeout(t);
      if (!r.ok) throw new Error("HTTP " + r.status + " for " + url);
      return r.text();
    })
    .catch((err) => {
      if (t) clearTimeout(t);
      throw err;
    });
}

// ─── Swap container ──────────────────────────────────────────────────────────
// The PDP is an IN-FLOW body swap (like #collection-modal), NOT a fixed overlay.
// The new product layout reveals its bottom panel by scrolling the DOCUMENT (the
// peek bar is position:sticky; top:50px), which a fixed overflow:hidden overlay
// would fight. So the container participates in normal flow: while it's shown the
// index sections (or the open collection modal) are display:none'd and this
// becomes the body's content, so the page scrolls IT. No position/overflow/
// transform and no scroll-lock — the document owns scrolling, exactly like the
// collection modal. Reused across opens (the fixed-overlay iOS touch-scroll
// recreate-per-open workaround no longer applies — there's no composited fixed
// inner scroller to re-layer).
function pmEnsureOverlay() {
  let el = document.getElementById(PRODUCT_MODAL.overlayId);
  if (el) return el;
  el = document.createElement("div");
  el.id = PRODUCT_MODAL.overlayId;
  // In-flow, full width, black canvas (the panel paints its own translucent bg;
  // the image area sits on black). No padding-top: it lands right after the ~50px
  // #index-logo flow slot, same as the home grid / collection modal.
  el.style.cssText =
    "display:none;width:100%;box-sizing:border-box;background:#000;color:#fff;" +
    "z-index:var(--z-product-modal);";
  document.body.appendChild(el);
  return el;
}

// ─── Cache-miss loading animation ─────────────────────────────────────────────
// On a cold open the underlying view fades out (0.10s) and then there's nothing
// to look at until the fetch resolves — on a slow network that gap is long enough
// to read as "broken". Fill it with the same drawing-square animation the grid
// uses (snippets/loading.liquid → .loading-square, animated in style-globals).
// White on the black body background, dead-centre of the viewport. Shown only on
// a real cache MISS, and only after a short delay so warm/fast opens never flash
// it. Removed the instant content is injected, or on any failure/close path.
const PM_LOADING_MARKUP =
  '<div class="loading-square">' +
  '<div class="border left-drawing"></div>' +
  '<div class="border bottom-drawing"></div>' +
  '<div class="border top-drawing"></div>' +
  '<div class="border right-drawing"></div>' +
  "</div>";

function pmShowLoader() {
  if (document.getElementById(PRODUCT_MODAL.loaderId)) return;
  const l = document.createElement("div");
  l.id = PRODUCT_MODAL.loaderId;
  // Full-viewport, flex-centred, click-through. Sits at the product-modal layer
  // so it's above the faded-out underlay; the overlay (same z, appended later in
  // DOM) paints over it when content lands, and we remove it at that moment.
  l.style.cssText =
    "position:fixed;inset:0;z-index:var(--z-product-modal);" +
    "display:flex;align-items:center;justify-content:center;" +
    "pointer-events:none;";
  l.innerHTML = PM_LOADING_MARKUP;
  document.body.appendChild(l);
}

function pmHideLoader() {
  if (PRODUCT_MODAL.loaderTimer) {
    clearTimeout(PRODUCT_MODAL.loaderTimer);
    PRODUCT_MODAL.loaderTimer = null;
  }
  const l = document.getElementById(PRODUCT_MODAL.loaderId);
  if (l) l.remove();
}

// ─── Fade helpers ───────────────────────────────────────────────────────────
// Fade EVERY direct child of <body> (header, content, message, footer,
// collection-modal, etc.) at the same time, EXCEPT our product modal. Uses
// opacity only — no visibility:hidden or display:none, so the layout stays
// intact and there's no shift.
function pmCollectionElements() {
  // The entire top nav UI is excluded — filter-bar section, logo, and
  // cart-count all stay put through the collection-modal ↔ PDP transition.
  // Any content change inside the nav (e.g. logo visibility flip when the
  // PDP modal opens) is a snap, not a fade. Cart-count, if present, simply
  // persists at opacity 1 across both directions.
  //
  // #cart-drawer / #cart-backdrop are also excluded: they're body-level
  // cart chrome whose visibility is owned by HeaderState (the .cart-open
  // class). If the fade-out pinned them to inline opacity:0, that inline
  // value would beat the non-!important `body.cart-open #cart-drawer`
  // reveal rule, so opening the cart over the PDP modal would show nothing.
  return Array.from(document.body.children).filter(
    (el) =>
      el.id !== PRODUCT_MODAL.overlayId &&
      // The PDP swap container manages its own fade-in/out — don't let the
      // underlay fade pin an inline opacity on it.
      el.id !== "pdp-swap" &&
      el.id !== "index-logo" &&
      el.id !== "cart-count" &&
      el.id !== "cart-drawer" &&
      el.id !== "cart-backdrop" &&
      el.id !== "cart-spacer-top" &&
      el.id !== "cart-spacer-bottom" &&
      el.id !== "shopify-section-index-filters" &&
      // Banner persists across the PDP modal until dismissed.
      el.id !== "shopify-section-index-message" &&
      // Nav background band is shown/faded via body.banner-open CSS, not the
      // modal fade — an inline opacity:0 here would beat that rule.
      el.id !== "nav-bg" &&
      el.tagName !== "SCRIPT"
  );
}

function pmFadeOutCurrentView(duration) {
  const els = pmCollectionElements();
  const dur = duration || "0.10s ease-out";
  els.forEach((el) => {
    el.style.transition = "opacity " + dur;
    el.style.opacity = "0";
  });
  return els;
}

function pmFadeInView(els) {
  if (!els || !els.length) return;
  els.forEach((el) => {
    el.style.transition = "opacity 0.9s ease-in-out";
    el.style.opacity = "1";
  });
  // Track this cleanup so a re-open within 950ms can cancel it.
  // Otherwise it would clear inline styles on the underlying view
  // we've just put back into "faded out" state for the next modal.
  if (PRODUCT_MODAL.fadeInTimer) clearTimeout(PRODUCT_MODAL.fadeInTimer);
  PRODUCT_MODAL.fadeInTimer = setTimeout(() => {
    PRODUCT_MODAL.fadeInTimer = null;
    els.forEach((el) => {
      el.style.transition = "";
      el.style.opacity = "";
    });
  }, 950);
}

// ─── Footer hand-off while the PDP is open ──────────────────────────────────
// The footer (sections/index-footer) renders on index, collection AND product
// (theme.liquid's gate), so a hard /products/x already has one in flow. The PDP
// swap opens over the INDEX, where hideSections() display:none's every
// shopify-section-* — the footer's own section included. So to match the hard
// page we MOVE the one real footer node into #pdp-swap (last child, after the
// panel) and re-insert it at its EXACT prior slot on close.
//
// Capturing the slot makes this location-agnostic: the footer may be at home OR
// moved into a collection/filter modal by script-footer-visibility.js. The
// isConnected check on restore yields to any closer that already re-homed it
// (e.g. goHomeFromPdpSwap → closeCollectionModal → __footerToHome), so we never
// double-insert or fight that path.
// ON again (2026-10-27, after a one-day removal): the footer is moved into
// #pdp-swap as its last child, after the panel, directly under the bottom bar
// (style-product.css, PDP footer). false = never moved; it stays in its
// home/collection slot, hidden with the rest of the view underneath. The hard
// product template has its own switch — the footer condition in theme.liquid —
// and the two are kept in step.
const PDP_FOOTER_ENABLED = true;

function pmFooterToPdp(container) {
  if (!PDP_FOOTER_ENABLED) return;
  if (!container) return;
  let f = PRODUCT_MODAL._footerNode;
  if (!f) {
    f = document.querySelector(".footer-wrapper");
    if (!f || !f.parentNode) return;
    // Capture the slot BEFORE the first move. hideSections() only sets display,
    // it doesn't reparent, so this still points at the live home/modal slot.
    PRODUCT_MODAL._footerNode = f;
    PRODUCT_MODAL._footerSlot = { parent: f.parentNode, next: f.nextSibling };
  }
  container.appendChild(f);
  // The PDP is taller than the viewport, so this resolves to unpinned — but ask
  // rather than assume: the node carries whatever pin state it had at capture.
  if (typeof window.__footerUpdate === "function") window.__footerUpdate();
}

// Detach (keeping our node ref alive) so an innerHTML wipe of #pdp-swap can't
// destroy the one real footer. Called before every rebuild — a family swap
// re-appends it right after; close hands it back via pmRestoreFooter.
function pmFooterDetachFromPdp(container) {
  const f = PRODUCT_MODAL._footerNode;
  if (f && container && container.contains(f)) f.remove();
}

function pmRestoreFooter() {
  if (!PDP_FOOTER_ENABLED) {
    // The footer never left its slot, but it sat in a display:none view while the
    // PDP was open, so any pin decision made then is stale. Re-measure now that
    // the view underneath is displayed again (same contract as below).
    if (typeof window.__footerUpdate === "function") window.__footerUpdate();
    return;
  }
  const f = PRODUCT_MODAL._footerNode;
  const slot = PRODUCT_MODAL._footerSlot;
  PRODUCT_MODAL._footerNode = null;
  PRODUCT_MODAL._footerSlot = null;
  if (!f || f.isConnected) return; // gone, or a closer already re-homed it
  if (slot && slot.parent && slot.parent.isConnected) {
    if (slot.next && slot.next.parentNode === slot.parent) {
      slot.parent.insertBefore(f, slot.next);
    } else {
      slot.parent.appendChild(f);
    }
    // Re-measure against the view we're returning to. The footer node carries
    // whatever pin state it had while it sat in the PDP; without this
    // it can stay pinned (position:fixed) over the now-restored tall VIEW ALL /
    // collection grid, hovering until the next manual scroll (bugs P2/P3).
    // CALLER CONTRACT: the container being restored into must already be
    // DISPLAYED — offsetTop reads 0 inside a display:none subtree, which makes
    // the pin decision unconditionally true (the footer-flicker bug). The swap
    // teardown therefore calls this after showing the modal / sections.
    // (__footerToHome's branch below re-measures on its own.)
    if (typeof window.__footerUpdate === "function") window.__footerUpdate();
  } else if (document.body) {
    // The prior container is gone (e.g. its modal was torn down) — fall back to
    // the footer's home slot so the node is never left orphaned.
    document.body.appendChild(f);
    if (typeof window.__footerToHome === "function") window.__footerToHome();
  }
}

// ─── Scoped style block ─────────────────────────────────────────────────────
function pmEnsureStyle() {
  if (document.getElementById(PRODUCT_MODAL.styleId)) return;
  const s = document.createElement("style");
  s.id = PRODUCT_MODAL.styleId;
  // Only NAV rules now. The PDP is an in-flow body swap, so style-product.css
  // styles its content directly (no overlay-scoped overrides, and the old
  // fixed-description / .product-description-container layout no longer exists).
  s.textContent = `
    /* #index-logo is already position:fixed at top on the index (pre-load.liquid);
       while the PDP is shown it's the close affordance — force it visible over the
       swap (the filter bar otherwise sets inline visibility:hidden on it). */
    body.product-modal-open #index-logo {
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      cursor: pointer !important;
      transition: none !important;
    }

    /* When the cart opens over the PDP, the logo persists as the close target —
       route pointer events: container none (negative space → close bar), the logo
       image re-enables them (→ home) via navigation.liquid's body.cart-open rule. */
    body.product-modal-open.cart-open #index-logo {
      pointer-events: none !important;
    }

    /* Filter bar swaps out (display:none, not visibility — the dropdowns force
       their own pointer-events, so they'd stay clickable otherwise). Instant, in
       the same frame the swap is shown; reverse snaps it back on close. */
    body.product-modal-open #index-menu,
    body.product-modal-open.cart-open #index-menu {
      display: none !important;
    }

    /* Restore pointer-events on the × icon when the cart is open over the PDP, so
       its toggle handler in script-cart.js can close the cart. */
    body.product-modal-open.cart-open #cart-count {
      pointer-events: auto !important;
      cursor: pointer !important;
    }
  `;
  document.head.appendChild(s);
}

// ─── style-product.css (only loaded while modal is open) ────────────────────
// Returns a Promise that resolves when the stylesheet has loaded, so we can
// inject the product markup AFTER its CSS is applied (preventing FOUC where
// images flash at default browser size before shrinking to their styled size).
function pmEnsureProductCss(linkFromFetched) {
  if (!linkFromFetched) return Promise.resolve();
  const existing = document.getElementById(PRODUCT_MODAL.cssId);
  if (existing) return Promise.resolve();
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.id = PRODUCT_MODAL.cssId;
    link.rel = "stylesheet";
    link.href = linkFromFetched.getAttribute("href");
    link.onload = resolve;
    link.onerror = resolve;
    document.head.appendChild(link);
  });
}

// ─── Parse fetched product page ─────────────────────────────────────────────
function pmExtract(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const wrapper = doc.querySelector(".product-page-wrapper");
  if (!wrapper) return null;

  // Eager-load product images. Shopify's product template marks them
  // `loading="lazy"`, which delays the fetch until layout decides the
  // image is near the viewport — fine on a real PDP, wrong inside a
  // modal that is the user's current focus. Also start each image at
  // opacity 0 with a short fade transition so it can be revealed
  // individually on its own load event (chrome doesn't have to wait).
  wrapper.querySelectorAll("img").forEach((img, idx) => {
    img.removeAttribute("loading");
    img.setAttribute("decoding", "async");
    if (idx === 0) img.setAttribute("fetchpriority", "high");
    const existing = img.getAttribute("style") || "";
    // Image fades in on its own `load` event, matched to the overlay
    // timing so a late-arriving image lands with the same easing as
    // the chrome fade-in.
    img.setAttribute(
      "style",
      existing + ";opacity:0;transition:opacity 0.5s ease-out;"
    );
    img.dataset.pmFadeImg = "1";
  });

  // The unified PDP layout drives everything through script-product.js. Find its
  // resolved URL so pmInitPdp can load it once (it self-initialises and exposes
  // window.__pdpInit for every subsequent open).
  let scriptSrc = "";
  doc.querySelectorAll("script[src]").forEach((s) => {
    if ((s.getAttribute("src") || "").indexOf("script-product.js") !== -1) {
      scriptSrc = s.src; // resolved against the parsed doc's base
    }
  });

  return {
    // .product-page-wrapper (images) + #product-panel (SKU/price/variant/add-to-
    // cart/description) are SIBLINGS in the new layout — inject both. The
    // #pdp-variants JSON tag is data read by script-product.js via getElementById;
    // injected via innerHTML it's inert (won't execute) but is findable.
    wrapper: wrapper.outerHTML,
    panel: doc.getElementById("product-panel")?.outerHTML || "",
    variantsJson: doc.getElementById("pdp-variants")?.outerHTML || "",
    styleLink: doc.querySelector('link[href*="style-product.css"]'),
    scriptSrc,
  };
}

// ─── Initialise the PDP script inside the swap ──────────────────────────────
// The unified script-product.js is built for this: it exposes window.__pdpInit
// and (since DOMContentLoaded already fired on the index) re-initialises against
// whatever #product-panel / #product-images / #pdp-variants are currently in the
// DOM. So we load it ONCE (the IIFE self-runs initPanel on inject), then just call
// window.__pdpInit() on every later open. The panel + JSON must already be in the
// DOM (injected by the caller) before this runs.
function pmInitPdp(scriptSrc) {
  // Already loaded (a previous open injected it): just re-init against the new
  // product's freshly-injected panel.
  if (typeof window.__pdpInit === "function") {
    window.__pdpInit();
    return Promise.resolve();
  }
  if (!scriptSrc) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = scriptSrc;
    // The IIFE runs initPanel() itself on load (readyState is already "complete"),
    // so by onload the PDP is wired. Call __pdpInit once more defensively in case
    // load raced the panel injection — initPanel is idempotent (re-queries fresh).
    s.onload = () => {
      if (typeof window.__pdpInit === "function") window.__pdpInit();
      resolve();
    };
    s.onerror = () => resolve();
    document.body.appendChild(s);
  });
}

// ─── Underlay hide / show (the body swap) ────────────────────────────────────
// While the PDP is shown, whatever was the document's content must be display:
// none'd so only the PDP is in flow and the page scrolls IT. Two underlay kinds:
//   • a collection / filter modal is open → hide just #collection-modal.
//   • otherwise (home grid) → hide the index sections (the closure exposes
//     __hideIndexSections / __showIndexSections, the same helpers the collection
//     modal uses). _fromCollection records which, so close restores the right one.
function pmSwapHideUnderlay() {
  const col = document.getElementById("collection-modal");
  if (col && col.style.display === "flex") {
    PRODUCT_MODAL._fromCollection = true;
    col.style.display = "none";
  } else {
    PRODUCT_MODAL._fromCollection = false;
    if (typeof window.__hideIndexSections === "function") window.__hideIndexSections();
  }
}

function pmSwapShowUnderlay() {
  if (PRODUCT_MODAL._fromCollection) {
    const col = document.getElementById("collection-modal");
    if (col) col.style.display = "flex";
  } else if (typeof window.__showIndexSections === "function") {
    window.__showIndexSections();
  }
  PRODUCT_MODAL._fromCollection = false;
}

// True while the PDP is shown OR mid-close (teardown timer pending). The
// collection modal's popstate handler defers to the PDP close in this window so
// it doesn't rebuild (and reset the scroll of) a collection the PDP close is
// about to restore.
function pmIsOpenOrClosing() {
  const o = document.getElementById(PRODUCT_MODAL.overlayId);
  return !!(o && (o.style.display === "block" || PRODUCT_MODAL.closeTimer));
}

// ─── Open ───────────────────────────────────────────────────────────────────
// In-flow body swap (like the collection modal): fade the current view out
// (0.10s), hide it, inject the PDP as the document's content, scroll to the top,
// and fade the PDP in (0.5s). The page — not a fixed overlay — owns scrolling.
function openProductModal(productUrl, opts) {
  opts = opts || {};
  // The user is opening a product — pause background warming so this open (and its
  // product-script fetches) get the network first. No-op until PM_WARM is defined.
  if (typeof pmWarmYield === "function") pmWarmYield(1500);
  // Cancel any still-pending close from a previous PDP: its teardown timer would
  // hide/clear the view we're about to show, and its fade-in timer would clobber
  // the underlay opacity we manage here. The swap container is REUSED (no fixed
  // overlay → no iOS recreate-per-open need), so don't remove it.
  if (PRODUCT_MODAL.closeTimer) {
    clearTimeout(PRODUCT_MODAL.closeTimer);
    PRODUCT_MODAL.closeTimer = null;
  }
  if (PRODUCT_MODAL.fadeInTimer) {
    clearTimeout(PRODUCT_MODAL.fadeInTimer);
    PRODUCT_MODAL.fadeInTimer = null;
  }
  // Clear any loader/loader-timer left by a previous (possibly interrupted) open.
  pmHideLoader();

  const overlay = pmEnsureOverlay();
  pmEnsureStyle();

  // Sequential fade (mirrors the collection modal): underlay out fast, PDP in.
  const FADE_OUT = "0.10s ease-out";
  const FADE_OUT_MS = 100;
  const FADE_IN = "0.5s ease-out";

  document.body.classList.add("product-modal-open");

  // alreadyOpen = a PDP is already shown (a re-open landing during a close, or a
  // popstate-driven open). The underlay is already hidden and the saved scroll /
  // faded view belong to the first open — keep them; just swap content + fade in.
  const alreadyOpen = overlay.style.display === "block";

  let fadedView;
  if (!alreadyOpen) {
    // Remember where the underlay (home grid OR collection) is, to restore on close.
    PRODUCT_MODAL._returnScrollY = window.scrollY || window.pageYOffset || 0;
    // Fade the current view out now (synchronously with the click). The underlay is
    // display:none'd only once content is ready (the swap step), so a failed fetch
    // never strands a blank page. Logo + cart-count are excluded from the fade
    // (pmCollectionElements) so they snap rather than fade across the transition.
    fadedView = pmFadeOutCurrentView(FADE_OUT);
    PRODUCT_MODAL._fadedView = fadedView;
  } else {
    fadedView = PRODUCT_MODAL._fadedView;
  }

  // Update the URL to the product page IMMEDIATELY (synchronously with the
  // click), not after the fetch resolves. If the open is slow and the user
  // refreshes before content lands, they get the real product page rather
  // than a stale index URL. Snapshot the pre-open URL so the failure paths
  // below can roll it back. _pmPushedUrl marks that we own this entry.
  const _pmPrevUrl = window.location.pathname + window.location.search;
  // opts.fromPopstate: the open is being driven by a back/forward navigation
  // INTO an existing productModal history entry. The browser already moved the
  // address bar to productUrl — pushing again would stack a duplicate entry (and
  // break a subsequent forward). Only push when this is a fresh, user-initiated open.
  if (!opts.fromPopstate) {
    history.pushState({ productModal: true, url: productUrl }, "", productUrl);
  }
  PRODUCT_MODAL._pmPushedUrl = productUrl;

  // 2. Fetch (or cache hit) runs in parallel.
  // Only treat a string in the cache as a real hit. In-flight Promises
  // from pmPrefetch live there too and should be awaited (not re-fetched).
  const cached = PRODUCT_MODAL.cache[productUrl];
  const htmlPromise =
    typeof cached === "string"
      ? Promise.resolve(cached)
      : cached && typeof cached.then === "function"
        ? cached
        : pmFetchOk(pmModalUrl(productUrl)).then((html) => {
            PRODUCT_MODAL.cache[productUrl] = html;
            return html;
          });

  // Cold open (no ready HTML in cache): the content has to come over the wire,
  // which can leave a visible gap after the underlay fades out. Schedule the
  // centred loading animation, but only after a short delay — a fast response
  // beats the timer and pmHideLoader() (called on inject/failure) cancels it, so
  // warm or quick opens never flash it.
  if (typeof cached !== "string") {
    PRODUCT_MODAL.loaderTimer = setTimeout(pmShowLoader, 150);
  }

  // Gate the swap on BOTH content-ready AND the fade-out having played, so a warm
  // open still shows the 0.10s underlay fade (mirrors the collection modal). A
  // re-open over an already-open PDP has nothing to fade out (gate 0).
  const fadeGate = new Promise((res) => setTimeout(res, alreadyOpen ? 0 : FADE_OUT_MS));

  Promise.all([
    htmlPromise.then((html) => {
      const data = pmExtract(html);
      if (!data) return null;
      // Await style-product.css so the panel + images land already styled (no
      // FOUC / reflow). Preloaded on the index, so a warm cache resolves instantly.
      return pmEnsureProductCss(data.styleLink).then(() => data);
    }),
    fadeGate,
  ])
    .then((results) => {
      const data = results[0];
      if (!data) {
        pmHideLoader();
        // Extraction failed and we never opened — roll the URL back to where the
        // user was so the address bar isn't left on a page we didn't show.
        if (PRODUCT_MODAL._pmPushedUrl === productUrl) {
          PRODUCT_MODAL._pmPushedUrl = null;
          try { history.replaceState({}, "", _pmPrevUrl); } catch (_) {}
        }
        // Undo the open so the user isn't stranded on a faded-out page.
        if (!alreadyOpen) {
          document.body.classList.remove("product-modal-open");
          if (fadedView) fadedView.forEach((el) => { el.style.transition = ""; el.style.opacity = ""; });
        }
        return;
      }

      // Content is here — drop the loading animation before we paint the PDP.
      pmHideLoader();

      // Commit the swap: hide the underlay (home sections OR the open collection
      // modal) NOW that we have content, so the in-flow PDP becomes the document's
      // content and the page scrolls IT. Done here — not before the fetch — so a
      // failed fetch never strands the page on a hidden underlay.
      if (!alreadyOpen) pmSwapHideUnderlay();

      // Inject while the container is at opacity 0 so it's fully painted before the
      // fade-in begins. wrapper (images) + panel (info) are siblings in the new
      // layout; the #pdp-variants tag is inert data read by script-product.js.
      overlay.style.display = "block";
      overlay.style.opacity = "0";
      overlay.style.transition = "";
      // Promote to its own compositor layer for the duration of the fade so the
      // 0.5s opacity animation composites instead of repainting the whole subtree
      // (images + panel) every frame. Cleared on transitionend below.
      overlay.style.willChange = "opacity";
      overlay.innerHTML = data.wrapper + data.panel + data.variantsJson;

      // Land at the top of the product (the panel reveals as the page scrolls down).
      window.scrollTo(0, 0);

      // Per-image fade-in on each image's own load (same behaviour as the grid).
      overlay.querySelectorAll("img[data-pm-fade-img]").forEach((img) => {
        const loader = img
          .closest(".img-and-loading-container, .media-and-loading-container")
          ?.querySelector(".image-loading");
        const reveal = () => {
          img.style.opacity = "1";
          if (loader) loader.style.display = "none";
        };
        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          if (loader) loader.style.display = "flex";
          img.addEventListener("load", reveal, { once: true });
          img.addEventListener("error", reveal, { once: true });
        }
      });

      PRODUCT_MODAL._pmPushedUrl = null;

      // Wire the PDP: variants / family soft-swap / add-to-cart / scroll indicator.
      // script-product.js exposes window.__pdpInit and re-initialises against the
      // freshly-injected panel (see pmInitPdp).
      pmInitPdp(data.scriptSrc);

      // Fade the PDP in. Wait TWO frames, not one: the first frame lets the
      // browser lay out and paint the just-injected subtree (wrapper + panel +
      // images); starting the transition on that same frame drops the opening
      // frames of the fade and reads as a choppy stutter. The second frame begins
      // the transition against fully-painted content, so it eases cleanly from 0.
      const startFade = () => {
        overlay.style.transition = "opacity " + FADE_IN;
        overlay.style.opacity = "1";
        // Drop the compositor-layer hint once the fade finishes so a static PDP
        // isn't held on its own layer while the user scrolls/reads it.
        const clear = (e) => {
          if (e.target !== overlay || e.propertyName !== "opacity") return;
          overlay.style.willChange = "";
          overlay.removeEventListener("transitionend", clear);
        };
        overlay.addEventListener("transitionend", clear);
      };
      requestAnimationFrame(() => requestAnimationFrame(startFade));
    })
    .catch((err) => {
      console.warn("[product-modal] open failed, falling back to navigation:", err && err.message);
      pmHideLoader();
      // Poisoned/failed cache entry must not survive — next click should retry.
      delete PRODUCT_MODAL.cache[productUrl];
      // Restore the faded-out underlay in case navigation is slow or cancelled.
      if (!alreadyOpen) {
        document.body.classList.remove("product-modal-open");
        if (fadedView) {
          fadedView.forEach((el) => { el.style.transition = ""; el.style.opacity = ""; });
        }
      }
      // Hard navigate so the user still reaches the product page rather than
      // being stuck on a faded screen.
      try { window.location.href = productUrl; } catch (_) {}
    });
}

// ─── Close ──────────────────────────────────────────────────────────────────
// Fade the PDP out (0.10s), then hide it, bring the underlay (home sections OR
// the collection modal) back into flow, restore the saved scroll, and fade it in.
// Mirrors the open transition in reverse.
function closeProductModal(goBack) {
  // If a back-navigation lands mid cold-open (container still hidden, loader up),
  // make sure the animation doesn't get stranded on screen.
  pmHideLoader();
  const overlay = document.getElementById(PRODUCT_MODAL.overlayId);
  if (!overlay || overlay.style.display === "none") return;

  // Re-entry guard. The logo click calls closeProductModal(true) → history.back(),
  // and the popstate handler then calls closeProductModal(false). Without this the
  // second call captures a now-null _fadedView and the underlay never fades back
  // in. If a close is already pending, only honor the goBack request and bail.
  if (PRODUCT_MODAL.closeTimer) {
    if (goBack) history.back();
    return;
  }

  const fadedView = PRODUCT_MODAL._fadedView;
  PRODUCT_MODAL._fadedView = null;

  overlay.style.transition = "opacity 0.10s ease-out";
  overlay.style.opacity = "0";

  // Restore the nav chrome synchronously (drop product-modal-open now, not in the
  // teardown timer) so the underlay reappears already in its correct header state
  // — no "logo, then title" flash. The style block's rules are all
  // body.product-modal-open-gated, so they no-op the instant the class is gone.
  document.body.classList.remove("product-modal-open");
  // The filter bar is back in the box model (product-modal-open's display:none is
  // gone). Re-measure filter-group widths in case a group was reset while hidden.
  if (window.__measureFilterWidths) window.__measureFilterWidths();
  const indexLogo = document.getElementById("index-logo");
  if (indexLogo) {
    indexLogo.style.transition = "";
    // The logo shows ONLY at the home top with nothing else in the bar. Keep it
    // HIDDEN when the filter bar is the nav content — i.e. we're returning to a
    // collection (_fromCollection) OR the home grid is scrolled (_returnScrollY).
    var scrolled = (PRODUCT_MODAL._returnScrollY || 0) > 0;
    if (PRODUCT_MODAL._fromCollection || scrolled) {
      indexLogo.style.opacity = "0";
      indexLogo.style.visibility = "hidden";
    } else {
      indexLogo.style.opacity = "";
      indexLogo.style.visibility = "";
    }
  }
  const cartCount = document.getElementById("cart-count");
  if (cartCount) {
    cartCount.style.transition = "";
    cartCount.style.opacity = cartCount.textContent.trim() === "" ? "0" : "1";
  }

  // Track the timer so a quick re-open can cancel it.
  if (PRODUCT_MODAL.closeTimer) clearTimeout(PRODUCT_MODAL.closeTimer);
  PRODUCT_MODAL.closeTimer = setTimeout(() => {
    PRODUCT_MODAL.closeTimer = null;
    // Hide + empty the PDP (the fade-out has finished, so no pop). Reused next open.
    overlay.style.display = "none";
    overlay.innerHTML = "";
    // Bring the underlay (home sections OR the collection modal) back into flow,
    // then restore the document scroll to where it was when we opened.
    pmSwapShowUnderlay();
    var _restoreY = PRODUCT_MODAL._returnScrollY || 0;
    PRODUCT_MODAL._returnScrollY = 0;
    window.scrollTo(0, _restoreY);
    // Drop the modal-scoped nav style + style-product.css (preloaded, so a re-open
    // re-adds it instantly; keeping it off the index avoids leaking PDP rules).
    document.getElementById(PRODUCT_MODAL.cssId)?.remove();
    document.getElementById(PRODUCT_MODAL.styleId)?.remove();
    pmFadeInView(fadedView);
  }, 100);

  if (goBack) history.back();
}

window.openProductModal = openProductModal;
window.closeProductModal = closeProductModal;

// Close the PDP and the collection/filter modal beneath it, landing on the bare
// home index-grid at the TOP. Used when the user clicks anywhere on the
// #index-logo strip while a PDP is open — the logo's "go home" semantics,
// matching every other template.
function goHomeFromProductModal() {
  // Was a collection / filter modal the underlay (now hidden beneath the PDP)?
  // Read the flag, not the DOM — pmSwapHideUnderlay display:none'd #collection-modal
  // on open, so its display is "none" here.
  const cameFromCollection = PRODUCT_MODAL._fromCollection;

  // Going HOME always lands at the TOP of the bare home grid. Pin both saved
  // scrolls to 0 before any restore below reads them.
  window.__indexHomeScrollY = 0;
  PRODUCT_MODAL._returnScrollY = 0;

  // Drive the nav chrome to its home-TOP state (centred logo, filter bar hidden)
  // via HeaderState.exitModal's at-top branch. Both flags gate that branch and are
  // CONSUMED by exitModal, so set them once and let exactly one path below run it.
  window._modalOpenedFromProductClick = true;
  window._preProductClickBarState = { atTop: true };

  if (cameFromCollection) {
    // Reset the (currently hidden) collection/filter underlay to the home state.
    // Its closer runs the instant (no-animation) reset: clears its grid, restores
    // the index sections via showSections, and calls HeaderState.exitModal().
    const fbar = document.getElementById("filter-bar");
    const filterActive = fbar && (
      fbar.classList.contains("name-active") ||
      fbar.classList.contains("category-finish-active") ||
      fbar.classList.contains("viewall-active")
    );
    if (filterActive && typeof window.closeFilterModal === "function") {
      window.closeFilterModal();              // instant
    } else if (typeof window.closeCollectionModal === "function") {
      window.closeCollectionModal(false);     // instant
    }
    // We're going HOME, not back to the collection — the closer above already
    // restored the index sections, so flip the flag so closeProductModal's
    // pmSwapShowUnderlay restores home (idempotent) instead of re-showing the
    // collection we just dismissed.
    PRODUCT_MODAL._fromCollection = false;
  } else if (window.HeaderState && typeof window.HeaderState.exitModal === "function") {
    // PDP opened DIRECTLY from the home grid (no collection underlay): drive the
    // same at-top restore here so a scrolled home grid comes back with the centred
    // logo rather than the filter bar.
    window.HeaderState.exitModal();
  }

  // Fade the PDP out — pmSwapShowUnderlay restores the home sections and the scroll
  // lands at the top (_returnScrollY is 0), revealing the reset home grid.
  closeProductModal(false);

  // Normalize the URL back to the bare index "/". Must be a literal "/", NOT
  // window.location.pathname: openProductModal pushState'd the product URL, so
  // by now the path IS "/products/x" — reading it back would just re-write the
  // product URL and the logo-home would never clear it. The instant closers above
  // run the updateHistory:false branch (no history write), so this is the only
  // normalization. script-index-product.js only loads on the index template
  // (via index-grid.liquid), so home is unambiguously "/".
  try { history.replaceState({}, "", "/"); } catch (_) {}
}

// Logo → home while a PDP swap is open. The CLICK itself is owned by
// script-navigation.js (full inversion: nobody binds handlers on nav elements
// but the nav). It dispatches on its own state and emits the semantic intent;
// this file only does the content work — the no-reload soft restore. The whole
// #index-logo strip (image AND negative space) lands on the bare home grid at
// the top, regardless of whether the PDP was opened from home or a collection.
document.addEventListener("nav:home", () => {
  if (typeof window.__pdpSwapGoHome === "function") window.__pdpSwapGoHome();
});

// ─── Hover prefetch ─────────────────────────────────────────────────────────
// Start fetching the product page when the user mouses over a card, so the
// HTML is already in the cache by the time they click. Cuts perceived modal
// open time to near zero for hover-then-click flows.
function pmPrefetch(url) {
  if (PRODUCT_MODAL.cache[url]) return;
  // Store the in-flight Promise so a click during prefetch awaits it
  // rather than firing a duplicate request. On non-OK (403 rate limit,
  // 404, network error), pmFetchOk throws — delete the entry so the
  // next attempt actually retries instead of replaying the failure.
  PRODUCT_MODAL.cache[url] = pmFetchOk(pmModalUrl(url))
    .then((html) => {
      PRODUCT_MODAL.cache[url] = html;
      // Warming is a bonus, never a failure: a throw here would reject the
      // cached Promise a click is awaiting and turn into a hard navigation.
      try { pmWarmProductScripts(html); } catch (e) { if (typeof console !== "undefined") console.warn("[swap] warm failed", e); }
      return html;
    })
    .catch((err) => {
      delete PRODUCT_MODAL.cache[url];
      throw err;
    });
}

// Drop a <link rel="prefetch"> for each product-page script the first
// time we see them referenced. pmRunScripts later calls fetch() on the
// same URLs; with the HTTP cache already warm, those fetches resolve
// instantly. Tens-of-KB win on the first user-initiated PDP open.
// The ONE way script-product.js gets onto the index. Promise-deduplicated: the
// warm step and the open both call this, and whichever is first appends the
// single tag; the other awaits the same promise. (Two tags with two spellings
// of the URL used to slip past a querySelector check and execute the script
// twice — the double initialisation behind "it animates in twice", and two
// instances each holding their own instruction-video group.)
let pmProductScriptPromise = null;
function pmLoadProductScript(src) {
  if (typeof window.__pdpInit === "function") return Promise.resolve();
  if (pmProductScriptPromise) return pmProductScriptPromise;
  if (!src) return Promise.resolve();
  pmProductScriptPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = src;
    tag.onload = resolve;
    tag.onerror = () => { pmProductScriptPromise = null; resolve(); }; // allow a retry
    document.body.appendChild(tag);
  });
  return pmProductScriptPromise;
}

const PRODUCT_SCRIPT_WARMED = Object.create(null);
function pmWarmProductScripts(html) {
  if (!html || typeof html !== "string") return;
  let doc;
  try { doc = new DOMParser().parseFromString(html, "text/html"); }
  catch (_) { return; }
  doc.querySelectorAll("script[src]").forEach((s) => {
    const src = s.getAttribute("src") || "";
    const isProductScript = src.indexOf("script-product.js") !== -1;
    if (!isProductScript) return;
    const absolute = s.src; // resolved against the parsed doc's base
    if (PRODUCT_SCRIPT_WARMED[absolute]) return;
    PRODUCT_SCRIPT_WARMED[absolute] = true;
    // EXECUTE it now, not just prefetch it. Its IIFE is a no-op without a
    // #product-panel in the DOM (initPanel bails on the first line) — all it
    // leaves behind is window.__pdpInit and the other __pdp* hooks, which is
    // exactly what runOrInitPdp needs to resolve synchronously. Without this the
    // first direct-link open of a session still paid script load + parse inside
    // the open.
    pmLoadProductScript(absolute);
  });
}

// Hover prefetch was removed: even debounced, a cursor settling on cards while
// browsing the grid fired speculative ?section_id=product-fragment requests that Shopify/
// Cloudflare flagged as bot traffic and 403'd. Prefetch now only fires on
// pointerdown (genuine press intent), and openProductModal fetches on demand
// if the cache is cold.

// ─── Never follow a product card's href ──────────────────────────────────────
// Direct-link cards carry a real href="/products/x" (index-grid.liquid) so they
// work as links for crawlers / new-tab gestures. For a plain click it must NEVER
// be followed: the swap IS the product page. The swap's click handler lives in
// the DOMContentLoaded closure below — any path that misses it (not bound yet,
// an earlier listener throwing) let the browser navigate. This capture-phase
// listener is bound at script evaluation and only does two things: cancel the
// default, and make sure an open happens. If the opener isn't bound yet the
// click is held until it is (see pmHeldClickUrl at window.__openProductSwap).
const HARD_NAV_GUARD_MS = 3500;
let pmHeldClickUrl = null;
document.addEventListener("click", (e) => {
  const a = e.target && e.target.closest && e.target.closest(".product-grid-image-container a[href]");
  if (!a) return;
  if (e.defaultPrevented) return;
  // new-tab / new-window gestures keep their native meaning
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const url = a.dataset.productUrl || a.getAttribute("href");
  if (!pmIsProductUrl(url)) return;
  e.preventDefault();
  const seqAtClick = PRODUCT_MODAL._openSeq;
  if (typeof window.__openProductSwap !== "function") pmHeldClickUrl = url;
  setTimeout(() => {
    if (PRODUCT_MODAL._openSeq !== seqAtClick) return; // an open started — the swap owns it
    if (typeof console !== "undefined") console.error("[swap] click never started an open", url);
  }, HARD_NAV_GUARD_MS);
}, true);

// Mobile has no hover, so the first tap pays the full fetch cost.
// pointerdown fires ahead of click on both touch and mouse, giving us
// an ~80–150ms head start before the click handler opens the modal.
document.addEventListener("pointerdown", (e) => {
  const a = e.target?.closest?.(".product-grid-image-container a");
  if (!a) return;
  const url = a.dataset.productUrl || a.getAttribute("href");
  if (pmIsProductUrl(url)) {
    pmWarmYield(1500); // this press is intent — let it (and the open) own the network
    pmPrefetch(url);
  }
}, true);

// ─── Background PDP warming (rate-limit-safe bulk prefetch) ───────────────────
// Eagerly warm the PDP HTML for products the user is likely to open next, so the
// modal opens instantly instead of paying the fetch on click. Two triggers feed
// this queue (see the calls below):
//   • index home grid — every DIRECT-link product (no tertiary collection, so a
//     click opens the PDP straight away).
//   • collection modal opens — every product shown in that collection (inside a
//     collection, every product click opens a PDP).
//
// Naive bulk prefetch was REMOVED once already (see the note above the pointerdown
// handler, and memory bulk-prefetch-disabled): firing N requests at once made
// Shopify/Cloudflare flag the burst as bot traffic and 403 every product — the
// "too many queries gets blocked" symptom. So this queue is deliberately gentle:
//   • ONE request in flight at a time — no parallel burst.
//   • a jittered gap between requests so the cadence isn't machine-regular.
//   • scheduled on idle time so warming never competes with first paint / images.
//   • a HARD STOP for the rest of the session on the first 403/429 — we're already
//     being throttled, so backing off is the only safe move. On-demand opens still
//     work because openProductModal fetches cold when the cache misses.
// Everything funnels through PRODUCT_MODAL.cache (shared with pointerdown prefetch
// and on-demand open), so a URL is never fetched twice.
var PM_WARM = {
  queue: [],
  queued: Object.create(null), // url → true once enqueued (dedupe across triggers / re-opens)
  active: false,               // a warm fetch is in flight (single-flight lock)
  stopped: false,              // tripped by a 403/429 — no more warming this session
  pauseUntil: 0,               // epoch ms; background warming holds off until then (click priority)
  minGapMs: 300,               // floor between requests
  jitterMs: 500,               // + up to this much randomness on top of the floor
};

function pmWarmEnqueue(urls) {
  if (PM_WARM.stopped || !urls || !urls.length) return;
  for (var i = 0; i < urls.length; i++) {
    var url = urls[i];
    if (!pmIsProductUrl(url)) continue;
    if (PM_WARM.queued[url]) continue;        // already queued this session
    if (PRODUCT_MODAL.cache[url]) continue;    // already warm (or in flight) via another path
    PM_WARM.queued[url] = true;
    PM_WARM.queue.push(url);
  }
  pmWarmPump();
}

function pmWarmPump() {
  if (PM_WARM.active || PM_WARM.stopped || !PM_WARM.queue.length) return;
  // A click/open just happened — hold off briefly so the user-driven fetch (and
  // its product scripts) get the network first. Reschedule for when the pause ends.
  var wait = PM_WARM.pauseUntil - Date.now();
  if (wait > 0) { setTimeout(pmWarmPump, wait); return; }
  PM_WARM.active = true;

  var fire = function () {
    if (PM_WARM.stopped) { PM_WARM.active = false; return; }
    // Skip any URLs that got warmed (by pointerdown / open) since being queued.
    var url = PM_WARM.queue.shift();
    while (url && PRODUCT_MODAL.cache[url]) url = PM_WARM.queue.shift();
    if (!url) { PM_WARM.active = false; return; }

    // Store the in-flight Promise in the shared cache so a click during warming
    // awaits it instead of firing a duplicate request (same pattern as pmPrefetch).
    PRODUCT_MODAL.cache[url] = pmFetchOk(pmModalUrl(url))
      .then(function (html) {
        PRODUCT_MODAL.cache[url] = html;
        try { pmWarmProductScripts(html); } catch (e) { if (typeof console !== "undefined") console.warn("[swap] warm failed", e); }
        return html;
      })
      .catch(function (err) {
        delete PRODUCT_MODAL.cache[url];
        // 403 = bot block, 429 = rate limit. Either means we're pushing too hard;
        // stop ALL further warming for the session rather than dig in deeper.
        var msg = (err && err.message) || "";
        if (msg.indexOf("403") !== -1 || msg.indexOf("429") !== -1) {
          PM_WARM.stopped = true;
          PM_WARM.queue.length = 0;
        }
      })
      .then(function () {
        PM_WARM.active = false;
        if (PM_WARM.stopped || !PM_WARM.queue.length) return;
        // Jittered gap before the next one so the cadence doesn't look automated.
        var gap = PM_WARM.minGapMs + Math.floor(Math.random() * PM_WARM.jitterMs);
        setTimeout(pmWarmPump, gap);
      });
  };

  // Idle-schedule so warming yields to first paint and image decoding. Falls back
  // to a plain timeout where requestIdleCallback is unavailable (older Safari).
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fire, { timeout: 2000 });
  } else {
    setTimeout(fire, 0);
  }
}

// A genuine click/open takes priority over background warming: pause the queue
// for a beat so the user's fetch (+ its product scripts) get the network first.
// The in-flight warm request can't be cancelled, but no NEW one starts until the
// pause ends. Called on pointerdown over a card and at the top of openProductModal.
function pmWarmYield(ms) {
  PM_WARM.pauseUntil = Date.now() + (ms || 1200);
}

// ─── Instruction-video warming ───────────────────────────────────────────────
// The PDP refuses to start its instruction videos until EVERY one of them can
// play through (script-product.js), so arriving at a product cold is the single
// slowest part of opening it — the HTML is warm, the video is not. The tiles
// carry their video URLs (data-instruction-videos, index-grid.liquid), so the
// files can be pulled into the HTTP cache while the grid is just sitting there.
//
// This must not cost the index anything, which shapes every choice below:
//   • <link rel="prefetch">, not fetch() and not a hidden <video>. Prefetch is
//     the browser's LOWEST priority class and is explicitly the "you may need
//     this next" hint — it queues behind every image, script and stylesheet this
//     page actually needs. fetch() would compete at normal priority, and a
//     detached <video preload=auto> would spin up a decode pipeline per file.
//   • only after `load`, and then only on idle time.
//   • ONE at a time with a gap, same reasoning as PM_WARM: a burst is what got
//     the old bulk prefetch 403'd.
//   • hard caps — PM_VIDEO.max files total, and nothing at all on a metered or
//     slow connection. These are multi-megabyte files; warming the whole
//     catalogue would be worse than the problem it solves.
//   • yields to a real click through the same PM_WARM.pauseUntil the HTML queue
//     uses, so opening a product is never slowed down by warming another one.
var PM_VIDEO = {
  queue: [],
  seen: Object.create(null),   // url → true (dedupe across triggers)
  started: 0,                  // how many have been handed to the browser
  max: 6,                      // ...and the ceiling on that, for the session
  active: false,
  minGapMs: 600,
  jitterMs: 600,
};

// Metered / slow connections are opted out entirely: this is a speculative
// download of something the user may never look at.
function pmVideoAllowed() {
  if (PM_VIDEO.started >= PM_VIDEO.max) return false;
  var c =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (c) {
    if (c.saveData) return false;
    var t = c.effectiveType || "";
    if (t === "slow-2g" || t === "2g" || t === "3g") return false;
  }
  return true;
}

function pmVideoEnqueue(urls) {
  if (!urls || !urls.length || !pmVideoAllowed()) return;
  for (var i = 0; i < urls.length; i++) {
    var url = urls[i];
    if (!url || PM_VIDEO.seen[url]) continue;
    PM_VIDEO.seen[url] = true;
    PM_VIDEO.queue.push(url);
  }
  pmVideoPump();
}

function pmVideoPump() {
  if (PM_VIDEO.active || !PM_VIDEO.queue.length || !pmVideoAllowed()) return;

  // Nothing speculative before the page it belongs to has finished loading.
  if (document.readyState !== "complete") {
    window.addEventListener("load", pmVideoPump, { once: true });
    return;
  }
  // A click just happened — let the user's own fetches have the network.
  var wait = PM_WARM.pauseUntil - Date.now();
  if (wait > 0) { setTimeout(pmVideoPump, wait); return; }

  PM_VIDEO.active = true;
  var fire = function () {
    var url = PM_VIDEO.queue.shift();
    if (!url || !pmVideoAllowed()) { PM_VIDEO.active = false; return; }

    var link = document.createElement("link");
    link.rel = "prefetch";
    /* No `as`: "video" is not a destination browsers accept on a prefetch hint,
       and naming an unsupported one gets the whole link ignored with a console
       warning. Without it the request still lands in the HTTP cache, which is
       all the <video> needs later. */
    link.href = url;
    document.head.appendChild(link);
    PM_VIDEO.started++;

    /* Fire-and-forget: there is no reliable completion signal for a prefetch
       link, and none is wanted — if it does not finish, the PDP fetches the
       video itself exactly as it does today. Space the next one out. */
    PM_VIDEO.active = false;
    if (!PM_VIDEO.queue.length) return;
    var gap = PM_VIDEO.minGapMs + Math.floor(Math.random() * PM_VIDEO.jitterMs);
    setTimeout(pmVideoPump, gap);
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fire, { timeout: 4000 });
  } else {
    setTimeout(fire, 0);
  }
}

// Pull the tile's video list off a product container, if it has one.
function pmVideoUrlsFor(container) {
  if (!container || !container.dataset) return null;
  /* The rendition this layout will play — index-grid.liquid carries one list per
     layout, chosen by the same rule product-body's <source>s use (the
     instruction-video-sources snippet), on the same 573px line as videoIsMobile. */
  var mobile = typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 573px)").matches;
  var raw = (mobile && container.dataset.instructionVideosMobile) ||
    container.dataset.instructionVideos;
  if (!raw) return null;
  return raw.split(",").filter(Boolean);
}

// ─── Warm-on-display (IntersectionObserver) ──────────────────────────────────
// Rather than warming the entire grid up front, warm each product only once it
// has been DISPLAYED — scrolled into view. Above-the-fold items intersect right
// away (warmed immediately); below-the-fold items warm as the user scrolls to
// them. Warming is one-shot per element (unobserve on first intersection), and
// everything still flows through the throttled PM_WARM queue. rootMargin warms a
// little BEFORE an item is fully on-screen so it's usually ready by click time.
var PM_WARM_IO = {
  observer: null,
  modalNodes: typeof Set === "function" ? new Set() : null, // collection clones currently observed
};

function pmWarmGetObserver() {
  if (PM_WARM_IO.observer || typeof IntersectionObserver !== "function") {
    return PM_WARM_IO.observer;
  }
  PM_WARM_IO.observer = new IntersectionObserver(
    function (entries) {
      var urls = [];
      var videos = [];
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var c = entry.target;
        PM_WARM_IO.observer.unobserve(c); // one-shot
        if (PM_WARM_IO.modalNodes) PM_WARM_IO.modalNodes.delete(c);
        if (c.dataset && c.dataset.productUrl) urls.push(c.dataset.productUrl);
        var vids = pmVideoUrlsFor(c);
        if (vids) videos.push.apply(videos, vids);
      });
      if (urls.length) pmWarmEnqueue(urls);
      /* Second, and separately: the HTML queue is what makes the modal open at
         all, so it goes first and the videos trail behind it. */
      if (videos.length) pmVideoEnqueue(videos);
    },
    { rootMargin: "300px" }
  );
  return PM_WARM_IO.observer;
}

// Observe a set of product containers so each warms when displayed. isModal=true
// tracks the nodes (collection clones) so they can be released when the modal
// rebuilds or closes — buildModalGrid throws those nodes away each open, and a
// never-intersected node would otherwise stay pinned by the observer.
function pmWarmObserve(containers, isModal) {
  if (PM_WARM.stopped || !containers || !containers.length) return;
  var obs = pmWarmGetObserver();
  if (!obs) {
    // No IntersectionObserver (old Safari): fall back to enqueuing them all — the
    // throttled queue still bounds the request rate.
    pmWarmEnqueue(
      containers.map(function (c) { return c && c.dataset ? c.dataset.productUrl : null; })
    );
    var videos = [];
    containers.forEach(function (c) {
      var vids = pmVideoUrlsFor(c);
      if (vids) videos.push.apply(videos, vids);
    });
    pmVideoEnqueue(videos);   // PM_VIDEO.max still bounds this
    return;
  }
  containers.forEach(function (c) {
    if (!c || !c.dataset || !c.dataset.productUrl) return;
    obs.observe(c);
    if (isModal && PM_WARM_IO.modalNodes) PM_WARM_IO.modalNodes.add(c);
  });
}

// Release the collection-clone observations before a rebuild / on close, so the
// observer doesn't pin detached clone nodes after modalInner is wiped.
function pmWarmReleaseModalObservations() {
  if (!PM_WARM_IO.observer || !PM_WARM_IO.modalNodes) return;
  PM_WARM_IO.modalNodes.forEach(function (c) { PM_WARM_IO.observer.unobserve(c); });
  PM_WARM_IO.modalNodes.clear();
}

// ─── Global listeners ───────────────────────────────────────────────────────

// NOTE: do NOT set history.scrollRestoration = 'manual' here. It's a per-DOCUMENT
// setting, so leaving the index on 'manual' (e.g. the user opens a PDP then
// navigates off to /pages/information instead of closing it) kills native scroll
// restore for the whole page — and script-navigation.js's reconcileOnLoad infers
// the logo/filter-bar state from whether a scroll event fires after load, so it
// then mis-reconciles the nav on every return. closeProductSwap pins the PDP for
// its fade instead, which makes the browser's restore harmless.

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  // If the cart is open over the PDP, Escape closes ONLY the cart — let
  // script-cart.js's Escape handler take it, and don't close the PDP too.
  if (window.HeaderState && window.HeaderState.state.cartOpen) return;
  // Escape = browser back, so history stays consistent (back → collection/home).
  // The popstate handler below then runs __closeProductSwap.
  if (window.__pdpSwapIsOpen && window.__pdpSwapIsOpen()) history.back();
});

window.addEventListener("popstate", (e) => {
  const st = e.state;

  // PDP body-swap (and in-PDP family swaps): re-open/swap to that product in-flow.
  if (st && (st.pdpSwap || st.pdpFamily) && st.url) {
    if (window.__openProductSwap) window.__openProductSwap(st.url, { push: false });
    return;
  }
  // Stepped off a PDP swap onto something else → close it. The collection popstate
  // handler then restores the right underlying view (collection grid, or home).
  if (window.__pdpSwapIsOpen && window.__pdpSwapIsOpen()) {
    if (window.__closeProductSwap) window.__closeProductSwap();
  }
});

document.addEventListener("DOMContentLoaded", function () {
  if (!document.body.classList.contains("template-index")) return;

  let isAnimating = false;
  let modalGeneration = 0;
  const STEP = 5;

  const fixedHeader = document.querySelector("header");
  const mainContent = document.getElementById("MainContent");

  const productGrid = document.getElementById("product-grid-main");

  // --- Fade in all home products once their images are ready ---
  // All products are already in the DOM via Liquid server-side rendering.
  (function fadeInHome() {
    const allHome = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    const homeImages = allHome
      .map((c) => c.querySelector(".product-grid-image"))
      .filter(Boolean);

    function doFade() {
      requestAnimationFrame(() => {
        allHome.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
        setTimeout(() => {
          allHome.forEach((c) => {
            c.style.transition = "";
            c.style.opacity = "";
          });
        }, 950);
      });
    }

    if (homeImages.length === 0) { doFade(); return; }

    let loadedCount = 0;
    homeImages.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        loadedCount++;
        if (loadedCount === homeImages.length) doFade();
      } else {
        img.addEventListener("load", () => {
          loadedCount++;
          if (loadedCount === homeImages.length) doFade();
        });
        img.addEventListener("error", () => {
          loadedCount++;
          if (loadedCount === homeImages.length) doFade();
        });
      }
    });
  })();

  // --- Warm direct-link PDPs on the home grid (as displayed) ---
  // Direct-link products (no tertiary collection) open the PDP straight from the
  // home grid on click, so pre-fetch their pages in the background — but only as
  // each is DISPLAYED (scrolled into view), not the whole grid at once. Items
  // above the fold warm immediately; the rest warm as the user scrolls to them.
  // Throttled through PM_WARM so the batch can't trip the bot/rate-limit block
  // that killed the previous bulk prefetch.
  (function warmHomeDirectPdps() {
    var directLink = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter(function (c) {
      return (
        c.dataset.inHome === "true" &&
        !(c.dataset.tertiaryHandles || "").trim() // no tertiary ⇒ direct PDP link
      );
    });
    pmWarmObserve(directLink, false);
  })();

  // --- Footer logo → soft scroll-to-top (home grid only) ---
  // On the home page the footer logo used to hard-link to "/", reloading the
  // page just to land back at the top. Instead, fade the home grid out, jump to
  // the top, and fade it back in — the scrollTo(0,0) re-triggers the scroll
  // sentinel, which restores the centre logo and hides the filter bar. Only the
  // home grid view qualifies: when a collection / VIEW ALL modal is open the
  // same footer node lives inside it, and there the logo keeps its default link.
  function goHomeTopFade() {
    const homeContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    homeContainers.forEach((c) => {
      c.style.transition = "opacity 0.3s ease-out";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      window.scrollTo(0, 0);
      // ONE authoritative nav ping: home top, with the logo on the SAME 0.9s
      // curve as the grid (logoRevealMs). The nav renders the whole chrome
      // change in this task — bar out, logo timed-reveal — so no intermediate
      // chrome state can ever paint.
      window.HeaderState.set({ view: "home", atTop: true }, { logoRevealMs: 900 });
      requestAnimationFrame(() => {
        homeContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
        setTimeout(() => {
          homeContainers.forEach((c) => { c.style.transition = ""; c.style.opacity = ""; });
        }, 950);
      });
    }, 300);
  }

  // Footer logo → home from INSIDE a collection / filter / VIEW ALL modal.
  // Same soft restore + animation family as goHomeTopFade (0.3s out, land on
  // the home TOP behind black, 0.9s grid fade-in with the logo on the same
  // curve) — this used to fall through to the <a href="/">'s HARD navigation,
  // where the persisted nav-hidden flag (correct for the collection being
  // left) pre-paint-hid the logo on the fresh index and reconcileOnLoad
  // popped it in ~300ms later with no fade.
  function goHomeFromModalFooter() {
    const modalContainers = Array.from(
      modalInner.querySelectorAll(".product-grid-image-container")
    );
    modalContainers.forEach((c) => {
      c.style.transition = "opacity 0.3s ease-out";
      c.style.opacity = "0";
    });
    setTimeout(() => {
      window.__indexHomeScrollY = 0; // land at the top
      // Instant closers (same pair as goHomeFromPdpSwap's underlay reset).
      const fbar = document.getElementById("filter-bar");
      const filterActive = fbar && (
        fbar.classList.contains("name-active") ||
        fbar.classList.contains("category-finish-active") ||
        fbar.classList.contains("viewall-active")
      );
      if (filterActive && typeof window.closeFilterModal === "function") {
        window.closeFilterModal();
      } else {
        closeModal(false);
      }
      window.scrollTo(0, 0);
      // ONE authoritative nav ping, AFTER the closers (whose own home pings
      // ran in this same task): force re-render with the timed logo reveal.
      // The renderer restarts the logo at opacity 0 before the next paint, so
      // the closers' instant chrome snaps never reach the screen — this is
      // what the pre-ping __revealLogoTimed guard hack could not guarantee.
      window.HeaderState.set(
        { view: "home", atTop: true },
        { logoRevealMs: 900, force: true }
      );
      // Home grid: paint transparent once, then the standard 0.9s fade-in.
      const homeContainers = Array.from(
        document.querySelectorAll("#product-grid-main .product-grid-image-container")
      ).filter((c) => c.dataset.inHome === "true");
      homeContainers.forEach((c) => {
        c.style.transition = "none";
        c.style.opacity = "0";
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          homeContainers.forEach((c) => {
            c.style.transition = "opacity 0.9s ease-in-out";
            c.style.opacity = "1";
          });
          setTimeout(() => {
            homeContainers.forEach((c) => { c.style.transition = ""; c.style.opacity = ""; });
          }, 950);
        });
      });
      // The instant closers don't touch history — normalize the modal URL
      // (?collection=/?name=/?viewall=) back to the locale root ourselves.
      try {
        history.replaceState(
          {},
          "",
          (window.Shopify && Shopify.routes && Shopify.routes.root) || "/"
        );
      } catch (_) {}
    }, 300);
  }

  document.addEventListener("click", function (e) {
    const link = e.target.closest && e.target.closest(".footer-logo-link");
    if (!link) return;
    const pdp = document.getElementById(PRODUCT_MODAL.overlayId);
    if (pdp && pdp.style.display !== "none" && pdp.style.display !== "") return;
    e.preventDefault();
    if (modal && modal.style.display === "flex") goHomeFromModalFooter();
    else goHomeTopFade();
  });

  // --- Check if a container belongs to a tertiary collection ---
  function hasHandle(container, handle) {
    var handles = (container.dataset.tertiaryHandles || "").split(",").map(function (h) { return h.trim(); });
    return handles.indexOf(handle) !== -1;
  }

  // --- Header helpers ---
  function forceHeaderVisible() {
    if (!fixedHeader) return;
    fixedHeader.style.transition = "opacity 0.3s ease-out";
    fixedHeader.style.opacity = "1";
    fixedHeader.style.pointerEvents = "auto";
    setTimeout(() => { fixedHeader.style.transition = ""; }, 300);
  }

  function restoreHeaderOpacity() {
    if (!fixedHeader) return;
    const contentSection = document.querySelector("#shopify-section-index-content");
    const spacerOffset = contentSection ? contentSection.offsetHeight : (window.innerHeight - 70);
    const progress = spacerOffset > 0 ? Math.min(window.scrollY / spacerOffset, 1) : 1;
    fixedHeader.style.opacity = progress;
    fixedHeader.style.pointerEvents = progress > 0.9 ? "auto" : "none";
  }

  // showModalTitle / hideModalTitle now live in script-functions.js (shared).

  // --- Modal setup ---
  // Use existing element if script-index-filters.js already created it,
  // otherwise create it here. Both scripts must reference the same DOM node.
  let modal = document.getElementById("collection-modal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "collection-modal";
    // In-flow body swap (not a fixed overlay): the modal participates in normal
    // document flow so the DOCUMENT scrolls over it, not an inner container. The
    // index sections are display:none'd while it's open (see hideSections), so
    // this becomes the body's content. No position/height/overflow — the page
    // owns scrolling. No padding-top: the modal sits right after the ~50px
    // #index-logo flow slot (kept via visibility:hidden), exactly like the index
    // grid does, so the collection lands in the same position as the home grid.
    // Shown as display:FLEX (column, flex:1 0 auto), never block: the body is a
    // min-height:100svh flex column, so the open modal grows to fill it and the
    // chain modal → #collection-modal-inner (flex:1) → .footer-wrapper
    // (margin-top:auto, style-index.css) seats the footer at the viewport bottom
    // on a short collection at EVERY width, with no scroll. A tall collection
    // packs it after the last row (auto = 0). Every open/close check in this
    // file and script-index-filters.js tests display === "flex" accordingly.
    modal.style.cssText =
      "display:none;flex-direction:column;flex:1 0 auto;width:100%;z-index:var(--z-collection-modal);background:transparent;opacity:1;box-sizing:border-box;";
    const modalInnerEl = document.createElement("div");
    modalInnerEl.id = "collection-modal-inner";
    modal.appendChild(modalInnerEl);
    document.body.appendChild(modal);
  }
  const modalInner = document.getElementById("collection-modal-inner");
  modalInner.style.display = "flex";
  modalInner.style.flexDirection = "column";
  // No min-height: the inner container sizes to its content exactly like the home
  // grid. A viewport-height floor here left a trailing blank block below the last
  // row on scrollable collections. Short collections bottom-align the footer via
  // the flex chain + margin-top:auto (see the cssText note above); on desktop
  // script-footer-visibility.js may additionally pin it (position:fixed), same seat.
  modalInner.style.minHeight = "";

  function appendModalFooter() {
    // No clone: MOVE the one real footer into the modal (last child, after the
    // grid). script-footer-visibility.js owns it and pins it (.footer-pinned)
    // only when the collection is shorter than the viewport — a tall collection
    // keeps the footer in-flow after the grid, exactly like the home page (the
    // old force-pin sat flush at the viewport bottom regardless of height).
    if (typeof window.__footerToModal === "function") window.__footerToModal(modalInner);
  }

  // --- Section visibility ---
  // display:none (not visibility:hidden) so the index sections fully leave the
  // document flow — the collection modal then becomes the body's content and the
  // document height/scrollbar reflect the collection, not the index grid behind it.
  function hideSections() {
    const root = mainContent || document;
    root.querySelectorAll("[id^='shopify-section-']").forEach((s) => {
      if (s.id === 'shopify-section-index-filters') return;
      // Banner persists across the collection modal until dismissed.
      if (s.id === 'shopify-section-index-message') return;
      s.style.display = "none";
    });
    document.querySelectorAll(".product-grid").forEach((g) => {
      if (!modal.contains(g)) g.style.display = "none";
    });
  }

  function showSections() {
    // Restore the index sections to flow FIRST, THEN re-home the footer. Order
    // matters: __footerToHome() re-measures the footer (script-footer-visibility
    // update()) to decide whether to pin it. If we re-home before the sections
    // are visible, it measures ~0 content above the footer and wrongly pins it
    // over the (about-to-reappear) tall home grid, where it hovers until the
    // next scroll forces a re-measure (bug P1). Restoring display first means
    // the re-measure sees the real home height. Safe vs. the old comment's
    // concern — restoring display does NOT clear modalInner.innerHTML, so the
    // one real footer node is still intact in the modal when we move it home.
    const root = mainContent || document;
    root.querySelectorAll("[id^='shopify-section-']").forEach((s) => {
      // SYMMETRIC WITH hideSections, which never hides the filter bar. See the
      // matching note in script-index-filters.js: the filter section's only
      // hider is hidePdpChrome(), paired with showPdpChrome() at the PDP
      // teardown. Un-hiding it here ran on the go-home CLICK, ~300ms before that
      // teardown, putting the bar back in the box model while the PDP was still
      // fading — the filter-bar-over-the-logo overlap.
      if (s.id === "shopify-section-index-filters") return;
      s.style.display = "";
    });
    document.querySelectorAll(".product-grid").forEach((g) => {
      if (!modal.contains(g)) g.style.display = "";
    });
    if (typeof window.__footerToHome === "function") window.__footerToHome();
  }

  // ─── PDP body swap (in-flow — replaces the index content, not a fixed overlay) ──
  // Grid product click → fetch the PDP fragment, hide the index sections, and
  // drop the real product-body (.product-page-wrapper + #product-panel) into the
  // page in normal flow, then run script-product.js. Mirrors the collection swap.
  let pdpSwapEl = null;
  let pdpSwapOpen = false;
  let pdpHomeScrollY = 0;
  let pdpFromCollection = false; // PDP opened over a collection/filter modal?
  let pdpGoHomeFade = false;     // logo go-home: close on the go-home family's curves
  let pdpSwapClosing = false;    // mid fade-out close (gates the collection popstate)
  let pdpFadeEls = null;         // outgoing view faded at open; opacity restored on close
  // Nav chrome (logo shown over the PDP as the go-home affordance, and its
  // restore on close) is fully owned by script-navigation.js now: this file
  // only PINGS the view — HeaderState.set({view:'pdp'}) on open, and
  // set({view: 'collection'|'home'}) after the close restores the underlay +
  // scroll (the nav re-derives atTop from the restored scroll position).

  function ensurePdpSwapEl() {
    if (pdpSwapEl) return pdpSwapEl;
    pdpSwapEl = document.createElement("div");
    pdpSwapEl.id = "pdp-swap";
    pdpSwapEl.style.cssText = "display:none;width:100%;box-sizing:border-box;";
    document.body.appendChild(pdpSwapEl);
    return pdpSwapEl;
  }

  // Index chrome that hideSections() keeps but the PDP swap should not show.
  // NOTE: #index-logo is intentionally NOT hidden — it stays visible over the
  // swap as the "go home" affordance (matching the hard PDP, where the fixed
  // logo floats over the product images). It's forced visible + clickable by
  // `body.pdp-swap-open #index-logo` in style-product.css and wired to
  // goHomeFromPdpSwap below.
  const PDP_HIDE_IDS = [
    "shopify-section-index-filters",
    "shopify-section-index-message",
  ];
  function hidePdpChrome() {
    PDP_HIDE_IDS.forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.style.display = "none";
    });
  }
  function showPdpChrome() {
    PDP_HIDE_IDS.forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.style.display = "";
    });
  }

  // The script is loaded BEFORE injection (see the open's data stage, through
  // pmLoadProductScript — the one deduplicated loader warming also uses), so
  // this is normally synchronous: one __pdpInit() against the new panel. With
  // no #product-panel in the DOM at load time the script's own self-init is a
  // no-op, so this call is the only init. The fallback only matters if that
  // load failed and is being retried.
  function runOrInitPdp(doc) {
    if (typeof window.__pdpInit === "function") {
      window.__pdpInit();
      return;
    }
    const s = doc.querySelector('script[src*="script-product.js"]');
    pmLoadProductScript(s ? s.src : "").then(() => {
      if (typeof window.__pdpInit === "function") window.__pdpInit();
    });
  }

  // Use the warm prefetch cache (filled on pointerdown + background warming) so
  // the click is instant; only fetch cold if nothing's cached yet.
  function pmSwapHtml(url) {
    const cached = PRODUCT_MODAL.cache[url];
    if (typeof cached === "string") return Promise.resolve(cached);
    if (cached && typeof cached.then === "function") return cached;
    pmPrefetch(url);
    const c = PRODUCT_MODAL.cache[url];
    return c && typeof c.then === "function" ? c : Promise.resolve(c);
  }

  function openProductSwap(productUrl, opts) {
    opts = opts || {};
    const push = opts.push !== false;
    if (!productUrl) return;
    ensurePdpSwapEl();
    const wasOpen = pdpSwapOpen;
    if (!wasOpen) pdpHomeScrollY = window.scrollY;

    // This open takes priority — pause background warming so its fetch (and the
    // product script) get the network first.
    pmWarmYield(1500);

    const FADE_OUT_MS = 100;
    // Fade the current view out (0.10s), exactly like the collection swap, then
    // fade the PDP in below. Skipped on a re-open / in-PDP family swap (a PDP is
    // already showing, so there's nothing behind to fade). pmFadeOutCurrentView
    // fades the index sections / open collection modal but leaves the nav chrome.
    if (!wasOpen) pdpFadeEls = pmFadeOutCurrentView("0.10s ease-out");

    // A previous open/close may still have a pending blur-restore; cancel it so
    // this open owns the blur-off → blur-on cycle cleanly.
    if (PRODUCT_MODAL.blurOnTimer) {
      clearTimeout(PRODUCT_MODAL.blurOnTimer);
      PRODUCT_MODAL.blurOnTimer = null;
    }

    // Loading state: show the spinner only if the content isn't ready within 150ms.
    // Warm-cache opens resolve first and clear the timer; cold opens get it.
    if (PRODUCT_MODAL.loaderTimer) clearTimeout(PRODUCT_MODAL.loaderTimer);
    PRODUCT_MODAL.loaderTimer = setTimeout(pmShowLoader, 150);

    // A click on a product card is NEVER a page load — the swap is the product
    // page. Fetch (retried once on failure; a 403/429 from the storefront's bot
    // filter clears better with a gap than a hammer), inject, fade. A fetch still
    // in flight is loading, not broken (fetchTimeoutMs is 30s for that reason).
    // Definitive failure restores the grid instead of navigating — see failSwap.
    const mySeq = ++PRODUCT_MODAL._openSeq;
    let swapLanded = false; // content injected — this open has produced a page
    const failSwap = (why, err) => {
      if (typeof console !== "undefined") console.error("[swap] open failed (" + why + ") — staying on the grid", err || "");
      if (PRODUCT_MODAL._openSeq !== mySeq) return; // another open took over
      if (swapLanded) return;                       // the swap did load — it owns the page
      pmHideLoader();
      if (!wasOpen && pdpFadeEls) pmFadeInView(pdpFadeEls);
    };
    const fetchWithRetry = () =>
      pmSwapHtml(productUrl).catch((err) => {
        if (typeof console !== "undefined") console.warn("[swap] fetch failed, retrying once", err);
        // pmPrefetch deleted the cache entry on failure, so this refetches.
        return new Promise((r) => setTimeout(r, 600)).then(() => pmSwapHtml(productUrl));
      });

    // Gate the swap on BOTH content-ready AND the fade-out having played, so a
    // warm-cache open still shows the 0.10s fade (mirrors the collection modal).
    Promise.all([
      fetchWithRetry().then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const wrapper = doc.querySelector(".product-page-wrapper");
        const panel = doc.querySelector("#product-panel");
        const varJson = doc.querySelector("#pdp-variants");
        const styleLink = doc.querySelector('link[href*="style-product.css"]');
        if (!wrapper || !panel) return null;
        // Apply style-product.css as a real stylesheet BEFORE injecting (the index
        // only PRELOADS it), and have script-product.js loaded BEFORE injecting
        // too: with no #product-panel in the DOM yet its load-time self-init is a
        // no-op, so the one __pdpInit() in runOrInitPdp is the only init. Both
        // resolve instantly when warm.
        const scriptTag = doc.querySelector('script[src*="script-product.js"]');
        return Promise.all([
          pmEnsureProductCss(styleLink),
          pmLoadProductScript(scriptTag ? scriptTag.src : ""),
        ]).then(() => ({ doc, wrapper, panel, varJson }));
      }),
      new Promise((res) => setTimeout(res, wasOpen ? 0 : FADE_OUT_MS)),
    ])
      .then((results) => {
        const data = results[0];
        if (!data) {
          // 200 but no product markup (challenge page, password page, a broken
          // fragment render). Don't cache it as valid for a later click.
          delete PRODUCT_MODAL.cache[productUrl];
          failSwap("fragment has no .product-page-wrapper / #product-panel");
          return;
        }
        if (PRODUCT_MODAL._openSeq !== mySeq) return; // a newer open owns the page
        swapLanded = true;
        pmHideLoader();

        if (!wasOpen) {
          // Remember whether we came from a collection / filter modal so close can
          // return to it (back → collection → home) instead of jumping straight home.
          pdpFromCollection = !!(modal && modal.style.display === "flex");
          hideSections();
          if (modal) modal.style.display = "none"; // hide an open collection swap
          // hideSections keeps the filter bar + message (for the collection swap);
          // the PDP wants a clean full-page context, so hide them too. The logo
          // is kept (see PDP_HIDE_IDS) as the go-home affordance.
          hidePdpChrome();
        }

        // Rescue the footer from the wipe (family swap re-entry), then re-append
        // it below the panel so the swap matches the hard product page.
        pmFooterDetachFromPdp(pdpSwapEl);
        // Release the outgoing panel BEFORE the wipe makes it unreachable: the
        // close path does wipe→teardown, but here the sync group's own rebuild
        // only sweeps members it still holds — the static player and the
        // panel refs would otherwise be dropped with their files still resident.
        if (wasOpen && typeof window.__pdpTeardown === "function") window.__pdpTeardown();
        pdpSwapEl.innerHTML = "";
        // IMPORTED, NOT ADOPTED. These nodes were built by DOMParser, in a document
        // with no browsing context. Appending them as-is MOVES them, and a <video>
        // created in that inert document can come across without ever binding to
        // the live page's media renderer: the clock and the readout run, the
        // picture stays black, and PLAY changes nothing. That was the instruction
        // videos on every home → product open, and never on a hard load (where the
        // live document's own parser made them). importNode builds fresh elements
        // owned by THIS document, exactly as the hard page does.
        if (data.varJson) pdpSwapEl.appendChild(document.importNode(data.varJson, true));
        pdpSwapEl.appendChild(document.importNode(data.wrapper, true));
        pdpSwapEl.appendChild(document.importNode(data.panel, true));
        // Fade the PDP in on the SAME curve as a collection→collection swap
        // (0.9s ease-in-out — see pmFadeInView and the collection grid fade at
        // openModal). Paint it fully first at opacity 0, then fade.
        //
        // INTO THE LAYOUT BEFORE ANYTHING MEASURES IT. #pdp-swap is display:none
        // between opens (ensurePdpSwapEl / closeProductSwap), and this block used
        // to come AFTER the footer hand-off and __pdpInit, so both measured a box
        // with no layout:
        //   - pmFooterToPdp → __footerUpdate read the footer's offsetTop as 0 and
        //     PINNED it (position:fixed, out of flow); the body ResizeObserver
        //     unpinned it a moment later, and the page grew by the footer's height
        //     after the tail under the videos had already been sized without it.
        //   - __pdpInit's own pass — the instruction grid's rect for
        //     --instruction-tail, the peek title for --pdp-peek — came back 0, so
        //     the tail was written from nothing.
        // Both were only corrected when the video group finished buffering, and
        // that correction was the page changing length while the videos were
        // still loading. At opacity 0 the box is laid out and paints nothing, so
        // measuring here costs no visible frame.
        pdpSwapEl.style.transition = "";
        pdpSwapEl.style.opacity = "0";
        pdpSwapEl.style.display = "";
        // De-jank: suppress the panel's backdrop-filter blur for the fade's
        // duration. An opacity animation over a live backdrop-filter re-rasterizes
        // the blur every frame (the stutter) — worse over the full 0.9s. Snapped
        // back on when the fade completes (unnoticeable: the panel is 0.8-opaque
        // black and mostly below the fold at reveal).
        pdpSwapEl.classList.add("pdp-blur-off");
        pmFooterToPdp(pdpSwapEl);
        pdpSwapOpen = true;
        document.body.classList.add("pdp-swap-open");
        if (!wasOpen) window.HeaderState.set({ view: "pdp" });
        window.scrollTo(0, 0);

        // hide the image-loading placeholders for already-decoded images and wire
        // the rest (initializeImageLoading never runs for injected content).
        // Scoped to the swap: an open must not re-walk (and re-listen) every
        // grid image on the page inside the click.
        if (typeof initializeImageLoading === "function") initializeImageLoading(pdpSwapEl);

        runOrInitPdp(data.doc);
        // On a cold first open the injected images are still decoding when the
        // fade starts, and each decode lands on the compositor mid-animation.
        // Wait for the first couple (the only ones on screen at reveal — the row
        // scrolls horizontally) to be decode-ready, capped at 250ms so a slow or
        // failed image can never hold the open hostage. Warm opens resolve
        // immediately, so this costs them nothing.
        const firstImgs = Array.from(
          pdpSwapEl.querySelectorAll(".product-page-image")
        ).slice(0, 2);
        const decoded = Promise.all(
          firstImgs.map((im) =>
            im.decode ? im.decode().catch(() => {}) : Promise.resolve()
          )
        );
        // ...then wait for the TEXT to be final too. Both webfaces are
        // font-display:block, so until they land the peek bar's title block is
        // laid out in fallback metrics and reflows when they arrive — which is
        // the intermittent shift in the bottom bar. script-product.js locks the
        // bar to --pdp-peek and only measures it once __textReady resolves, so
        // starting the fade after that means the reveal already carries the final
        // geometry. Capped at 600ms of its own: the fonts are preloaded, so this
        // is a no-op on any warm open, and a cold one can't be held hostage.
        const textSettled = window.__textReady || Promise.resolve();
        Promise.race([decoded, new Promise((r) => setTimeout(r, 250))])
          .then(() =>
            Promise.race([textSettled, new Promise((r) => setTimeout(r, 600))])
          )
          .then(() => {
          // Double-rAF = "after the next paint": let the injected layout (innerHTML +
          // runOrInitPdp) flush once before the fade starts, so its first frames run
          // smooth instead of landing on top of that layout work.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              pdpSwapEl.style.transition = "opacity 0.9s ease-in-out";
              pdpSwapEl.style.opacity = "1";
              // Restore the blur once the fade has played out (single rasterization
              // now, not per-frame). Tracked so a quick re-open / family swap cancels it.
              if (PRODUCT_MODAL.blurOnTimer) clearTimeout(PRODUCT_MODAL.blurOnTimer);
              PRODUCT_MODAL.blurOnTimer = setTimeout(() => {
                PRODUCT_MODAL.blurOnTimer = null;
                pdpSwapEl.classList.remove("pdp-blur-off");
                // Fade is done — deferred work that would have stuttered it can run
                // now (script-product.js's initFamily sibling warming listens here).
                window.dispatchEvent(new Event("pdp:revealed"));
              }, 900);
            });
          });
        });

        if (push) history.pushState({ pdpSwap: true, url: productUrl }, "", productUrl);
      })
      .catch((err) => {
        // A fetch that failed twice, or an error thrown anywhere in the chain
        // above: log the real reason and put the grid back.
        failSwap("exception / fetch failed after retry", err);
      });
  }

  // FREEZE THE PDP WHERE IT SITS. #pdp-swap is IN FLOW, so it shares the document
  // scroll — the old overlay PDP was position:fixed and physically could not move
  // while fading (see closeProductModal). Here, anything that touches scroll while
  // the PDP is still visible drags it along: the browser restoring the previous
  // history entry, the page height collapsing, or the underlay closers'
  // scrollTo(0, __indexHomeScrollY) on the go-home path. Pinning it fixed at its
  // current visual offset makes the close a pure opacity change again.
  //
  // MUST be called before any scroll-touching teardown, not just at the fade —
  // goHomeFromPdpSwap restores the underlay first, which is what made the logo
  // click visibly scroll up while the back button didn't. Idempotent.
  function pdpPinForFade() {
    const el = pdpSwapEl;
    if (!el || el.dataset.pinned === "1") return;
    el.dataset.pinned = "1";
    // DISARM THE VIDEO SCROLL-SNAP BEFORE THE LAYOUT CHANGES. style-product.css
    // puts `scroll-snap-type: y proximity` on <html> whenever an .instruction-grid
    // is in the document. Flipping this element to position:fixed collapses the
    // document, and Safari re-snaps the scroll toward the grid on that layout
    // change — clamped to the collapsed page's tiny max scroll (~190px). That
    // scroll fired AFTER the closer had reported home/top, so the nav truthfully
    // read "left the top" and faded the filter bar in over the go-home logo,
    // then snapped back when the teardown scrolled to 0. Only products whose
    // family carries instruction videos have the grid, which is why only those
    // showed it. html.video-snap-off is the class script-product.js uses for
    // "moving away — no snap"; its teardown clears it.
    document.documentElement.classList.add("video-snap-off");
    const y = window.scrollY || window.pageYOffset || 0;
    el.style.position = "fixed";
    el.style.top = -y + "px";
    el.style.left = "0";
    el.style.width = "100%";
  }

  function closeProductSwap() {
    if (!pdpSwapOpen) return;
    pdpSwapOpen = false;
    pdpSwapClosing = true; // gate the collection popstate while we fade + restore
    const el = pdpSwapEl;
    const fromCollection = pdpFromCollection;
    pdpFromCollection = false;
    const fadeEls = pdpFadeEls;
    pdpFadeEls = null;
    const goHomeFade = pdpGoHomeFade;
    pdpGoHomeFade = false;

    // Fade the PDP out (0.10s) IN PLACE, then bring the underlay back and fade it
    // in. Do NOT restore the index chrome (filter bar / banner) or drop
    // body.pdp-swap-open yet: those sit above #pdp-swap in flow, and re-adding
    // their height + the 50px logo padding while the PDP is still visible shoves
    // the fading PDP downward — the layout shift on back. Deferred to the teardown
    // below, once the PDP is display:none'd (so nothing visible moves).
    // 0.10s read as a hard cut — the panel/footer looked DELETED rather than
    // faded, especially against the 0.9s open. Matched to the open's curve so
    // back is the visual inverse of the click.
    // 100ms mirrors the OPEN (back = the click's visual inverse). The LOGO
    // go-home instead belongs to the go-home family (footer logos, collection
    // close), whose outgoing fade is 300ms — the 100ms cut there is what read
    // as "more abrupt than the rest of the page transitions".
    const CLOSE_FADE_MS = goHomeFade ? 300 : 100;
    // Give the instruction videos' files back NOW, not after the fade — but only
    // if the footage isn't on screen, which script-product.js decides (a run in
    // progress still waits for the teardown below). Two complete videos held
    // through a 300ms fade is the peak this page has, and it lands exactly as home
    // is restored and a collection grid is about to be built. See
    // releaseIdleVideos; __pdpTeardown after the fade is idempotent over it.
    if (typeof window.__pdpReleaseIdleVideos === "function") {
      window.__pdpReleaseIdleVideos();
    }
    if (el) {
      pdpPinForFade();
      // Same de-jank as the open: animating opacity over a live backdrop-filter
      // re-rasterizes the blur every frame. Cleared in the teardown below.
      el.classList.add("pdp-blur-off");
      el.style.transition = "opacity " + (CLOSE_FADE_MS / 1000) + "s ease-out";
      el.style.opacity = "0";
    }
    // Cancel any pending blur-restore so this close leaves no stray timer.
    if (PRODUCT_MODAL.blurOnTimer) {
      clearTimeout(PRODUCT_MODAL.blurOnTimer);
      PRODUCT_MODAL.blurOnTimer = null;
    }

    setTimeout(function () {
      // Hand the footer back to its pre-PDP slot BEFORE the wipe below destroys
      // it. Detach first: pmRestoreFooter's isConnected guard means "someone
      // else already re-homed it", and a footer still sitting in #pdp-swap reads
      // as connected — it would skip the restore and let the wipe eat it. Once
      // detached the guard reads true only when a closer (goHomeFromPdpSwap →
      // __footerToHome) genuinely got there first, which is what it's for.
      pmFooterDetachFromPdp(el);
      if (el) {
        el.style.display = "none";
        el.innerHTML = "";
        // The panel is gone; release what script-product.js bound on the WINDOW
        // for it (the instruction videos' scroll / resize handlers and their
        // state classes), or they keep running against a detached group on the
        // home page and can move the home scroll.
        if (typeof window.__pdpTeardown === "function") window.__pdpTeardown();
        el.style.transition = "";
        el.style.opacity = "";
        // release the fade-time pin (see pdpPinForFade) so the next open is in flow
        delete el.dataset.pinned;
        el.style.position = "";
        el.style.top = "";
        el.style.left = "";
        el.style.width = "";
        el.classList.remove("pdp-blur-off");
      }
      // PDP is hidden now — safe to restore the chrome + padding without shifting
      // anything visible (was the source of the on-back layout shift).
      document.body.classList.remove("pdp-swap-open");
      document.body.classList.remove("pdp-docked"); // drop the docked-nav background
      // script-product.js parks presentation state on <body> (pdp-compact is
      // ALWAYS on below 573px, plus the title/price fade classes and the
      // measured --pdp-peek). Sweep them with the swap so no PDP styling can
      // leak onto the index. (The footer-logo hider is no longer keyed to
      // pdp-compact — it's scoped to #pdp-swap / template-product in
      // style-product.css — so this sweep is hygiene, not load-bearing.)
      ["pdp-compact", "pdp-price-in", "pdp-price-fading", "pdp-title-in", "pdp-title-fading"]
        .forEach(function (c) { document.body.classList.remove(c); });
      document.body.style.removeProperty("--pdp-peek");
      showPdpChrome();
      let reContainers = null;
      let sheetExclude = null; // element whose reveal the container fade owns
      if (fromCollection && modal) {
        // Return to the collection/filter modal we came from — intact (only
        // display:none'd on open), so its scroll + grid are preserved.
        modal.style.display = "flex";
        // Restore with the SAME animation a collection OPEN uses: wrapper
        // solid immediately (title/chrome present), CONTAINERS fade 0→1 over
        // 0.9s. The old wrapper-fade (pmFadeInView on #collection-modal)
        // faded everything as one sheet — a different shape from every other
        // grid reveal, which read as wrong timing on back.
        modal.style.transition = "none";
        modal.style.opacity = "1";
        sheetExclude = modal;
        reContainers = Array.from(
          modalInner.querySelectorAll(".product-grid-image-container")
        );
        reContainers.forEach(function (c) {
          c.style.transition = "none";
          c.style.opacity = "0";
        });
      } else {
        showSections();
        if (goHomeFade) {
          // Logo go-home: reveal the home grid the way the whole go-home
          // family does — CONTAINERS fade 0→1 over 0.9s, not the section
          // wrapper as one sheet. The wrapper snaps solid here (invisible:
          // its containers are at 0 on black) and is excluded from the
          // element fade below so the two can't compound.
          const gridMain = document.getElementById("product-grid-main");
          const gridSection = gridMain && gridMain.closest("[id^='shopify-section-']");
          if (gridSection) {
            gridSection.style.transition = "none";
            gridSection.style.opacity = "";
            sheetExclude = gridSection;
          }
          reContainers = Array.from(
            document.querySelectorAll("#product-grid-main .product-grid-image-container")
          ).filter(function (c) { return c.dataset.inHome === "true"; });
          reContainers.forEach(function (c) {
            c.style.transition = "none";
            c.style.opacity = "0";
          });
        }
      }
      // Re-home the footer only NOW, with the view it lands in actually
      // displayed. pmRestoreFooter() re-measures the pin state on insert, and
      // doing that while the collection modal was still display:none read
      // offsetTop 0 → always "pinned" → the footer rode the 0.9s restore fade
      // stuck to the viewport bottom until the next ResizeObserver tick
      // unpinned it (the "footer logo flickers in" bug). On the home branch
      // showSections' __footerToHome has usually re-homed it already — then
      // this yields via its isConnected guard, exactly as designed.
      pmRestoreFooter();
      window.scrollTo(0, pdpHomeScrollY || 0);
      // Report the restored view — the nav derives its own chrome from it
      // (collection → bar shown / logo hidden; home → derived from the scroll
      // position just restored, so a go-home lands on the top logo state and a
      // back-to-scrolled-grid lands on the bar, with no stashed logo state).
      window.HeaderState.set({ view: fromCollection ? "collection" : "home" });
      // iOS applies its OWN history scroll restoration asynchronously — often
      // well after this teardown — yanking the document off the offset we just
      // restored (the "grid starts higher, then shifts down" bug: nothing in
      // THIS task can be ordered around a later write from the browser).
      // Hold the target for the settle window: any externally-moved frame is
      // snapped back before the next paint. User scrolling this soon after a
      // back-navigation isn't a real interaction pattern; the hold expires in
      // 450ms either way.
      (function holdScroll() {
        const target = pdpHomeScrollY || 0;
        const until = Date.now() + 450;
        (function tick() {
          if (Date.now() > until) return;
          if (Math.abs((window.scrollY || window.pageYOffset || 0) - target) > 1) {
            window.scrollTo(0, target);
          }
          requestAnimationFrame(tick);
        })();
      })();
      if (fadeEls || reContainers) {
        // The underlay was just re-displayed (display:none → visible) and still
        // carries the open's inline opacity:0. Starting the transition in the
        // SAME recalc as the display flip skips it — an element coming out of
        // display:none has no prior rendered state to transition from — so the
        // grid POPPED in instead of running the 0.9s fade every other swap
        // uses. Double-rAF (same staging as the cart open): let the transparent
        // state paint once, THEN start the fade, so the restore is the visual
        // inverse of the open like it was designed to be. Layout, scroll and
        // padding are already final by the first paint, so nothing can be seen
        // shifting — the grid fades in already in its correct place.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            // fromCollection: the modal wrapper is already solid — its reveal
            // is the container fade below, so keep the wrapper OUT of the
            // element fade (the rest of fadeEls — display:none'd home
            // sections — still get their inline opacity restored for later).
            if (fadeEls) {
              pmFadeInView(
                sheetExclude
                  ? fadeEls.filter(function (x) { return x !== sheetExclude; })
                  : fadeEls
              );
            }
            if (reContainers) {
              reContainers.forEach(function (c) {
                c.style.transition = "opacity 0.9s ease-in-out";
                c.style.opacity = "1";
              });
              setTimeout(function () {
                reContainers.forEach(function (c) {
                  // Yield if someone else has taken the containers over
                  // (same stale-cleanup discipline as the open's own timer).
                  if (c.style.opacity !== "1") return;
                  c.style.transition = "";
                  c.style.opacity = "";
                });
              }, 950);
            }
          });
        });
      }
      pdpSwapClosing = false;
    }, CLOSE_FADE_MS);
  }

  // Exposed for the (module-level) popstate handler.
  window.__openProductSwap = openProductSwap;
  // A direct-link click that landed before this closure was bound (see the
  // capture-phase guard at top level) was held rather than followed — open it.
  if (pmHeldClickUrl) {
    const held = pmHeldClickUrl;
    pmHeldClickUrl = null;
    openProductSwap(held);
  }
  window.__closeProductSwap = closeProductSwap;
  window.__pdpSwapIsOpen = function () { return pdpSwapOpen; };
  // True during the close's 100ms fade + teardown window. The popstate handler
  // in script-index-filters.js gates on this (as this file's own collection
  // handler does via the closure var): a back-from-PDP fires popstate for the
  // underlying modal entry BEFORE the teardown has restored the modal, and an
  // ungated rebuild there paints the grid while body.pdp-swap-open still has
  // padding-top:0 — the "grid starts without top padding, then shifts" bug.
  window.__pdpSwapIsClosing = function () { return pdpSwapClosing; };

  // Clicking the #index-logo strip while a PDP swap is open goes all the way
  // HOME — the whole strip (image AND surrounding negative space) lands on the
  // bare home grid at the TOP with fresh chrome (centred logo, filter bar
  // hidden), regardless of whether the PDP was opened from home or over a
  // collection/filter underlay. Soft restore — no reload.
  function goHomeFromPdpSwap() {
    if (!pdpSwapOpen) return;
    // Freeze the PDP FIRST: everything below (closeFilterModal /
    // closeCollectionModal / the scroll resets) moves the document while the PDP
    // is still on screen, so without this you watch it scroll up before the fade.
    // closeProductSwap's own call is then a no-op (idempotent).
    pdpPinForFade();
    const cameFromCollection = pdpFromCollection;
    pdpFromCollection = false;     // close restores home, not the collection underlay
    pdpHomeScrollY = 0;            // land at the top
    window.__indexHomeScrollY = 0;
    if (cameFromCollection) {
      // Reset the (hidden) collection/filter underlay back to the home state; its
      // instant closer clears the grid, runs showSections + HeaderState.exitModal.
      const fbar = document.getElementById("filter-bar");
      const filterActive = fbar && (
        fbar.classList.contains("name-active") ||
        fbar.classList.contains("category-finish-active") ||
        fbar.classList.contains("viewall-active")
      );
      if (filterActive && typeof window.closeFilterModal === "function") {
        window.closeFilterModal();            // instant; pings view:'home' itself
      } else if (typeof window.closeCollectionModal === "function") {
        window.closeCollectionModal(false);   // instant; pings view:'home' itself
      }
    } else {
      // Came from the bare home grid: report the home-top landing now (the
      // teardown ping in closeProductSwap will be a no-op re-assert).
      window.HeaderState.set({ view: "home", atTop: true });
    }
    pdpGoHomeFade = true; // close on the go-home family's curves (0.3s out, container 0.9s in)
    closeProductSwap();
    // Normalize the URL back to the index root (openProductSwap pushed the
    // product URL). Locale-aware (Shopify.routes.root → "/en/" on the English
    // locale) so a subsequent reload doesn't bounce through the language
    // router; never location.pathname (that would keep the product URL).
    try {
      history.replaceState(
        {},
        "",
        (window.Shopify && Shopify.routes && Shopify.routes.root) || "/"
      );
    } catch (_) {}
  }
  window.__pdpSwapGoHome = goHomeFromPdpSwap;

  // buildModalGrid now lives in script-functions.js (shared); call it with this
  // file's modalInner + appendModalFooter.

  // --- Animated open ---
  function animateAndOpen(tertiaryHandle, tertiaryTitle) {
    isAnimating = true;
    const myGeneration = ++modalGeneration;

    // Capture the home grid scroll before we swap the body out, so closing
    // restores the exact position. Only when opening fresh (not already in a
    // modal) — a filter change / rebuild while open must not overwrite it.
    if (modal.style.display !== "flex") window.__indexHomeScrollY = window.scrollY;

    const homeContainers = Array.from(
      document.querySelectorAll(".product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    // Fade out home grid
    homeContainers.forEach((c) => {
      c.style.transition = "opacity 0.10s ease-out";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      if (myGeneration !== modalGeneration) return;

      // Restore home containers (hidden by hideSections below)
      homeContainers.forEach((c) => {
        c.style.transition = "none";
        c.style.opacity = "";
      });
      isAnimating = false;

      // ensureSecondaryState() (force body to the secondary scroll position) is
      // no longer called: with the index sections display:none'd the body swap
      // makes buildModalGrid's scrollTo(0,0) the single source of scroll truth.
      hideSections();
      forceHeaderVisible();
      showModalTitle(tertiaryTitle);

      const params = new URLSearchParams(window.location.search);
      params.set("collection", tertiaryHandle);
      history.pushState(
        { collectionModal: true, handle: tertiaryHandle, title: tertiaryTitle },
        "",
        window.location.pathname + "?" + params.toString()
      );

      modal.style.transition = "none";
      modal.style.opacity = "1";
      modal.style.display = "flex";

      // Get all products that belong to this tertiary collection
      const sourceContainers = Array.from(
        document.querySelectorAll("#product-grid-main .product-grid-image-container")
      ).filter((c) => hasHandle(c, tertiaryHandle));

      const modalContainers = buildModalGrid(sourceContainers, modalInner, appendModalFooter);

      // Inside a collection every product click opens a PDP, so warm them — but as
      // each is DISPLAYED, not all at once. Release the previous collection's
      // tracked observations first (buildModalGrid just discarded those clones).
      pmWarmReleaseModalObservations();
      pmWarmObserve(modalContainers, true);

      requestAnimationFrame(() => {
        modalContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });
    }, 100);
  }

  // --- Simple open (popstate / auto-open) ---
  function openModal(tertiaryHandle, tertiaryTitle, { instant = false, pushHistory = true } = {}) {
    // Capture home scroll before the swap (only on a fresh open). buildModalGrid
    // then resets to top; closeModal restores this value.
    if (modal.style.display !== "flex") window.__indexHomeScrollY = window.scrollY;
    // Report the view — the nav snaps its own chrome (bar shown / logo hidden)
    // from this one ping, on every open path: click, deep link, popstate.
    window.HeaderState.set({ view: "collection" });
    hideSections();

    const earlyStyle = document.getElementById("cl-deeplink-hide");
    if (earlyStyle) earlyStyle.remove();

    const sourceContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => hasHandle(c, tertiaryHandle));

    buildModalGrid(sourceContainers, modalInner, appendModalFooter);

    // Warm this collection's products as they're displayed (each click → a PDP).
    pmWarmReleaseModalObservations();
    pmWarmObserve(
      Array.from(modalInner.querySelectorAll(".product-grid-image-container")),
      true
    );

    forceHeaderVisible();
    showModalTitle(tertiaryTitle || tertiaryHandle);

    modal.style.background = "transparent";

    if (instant) {
      modal.style.transition = "none";
      modal.style.opacity = "1";
      modal.style.display = "flex";

      const modalContainers = Array.from(
        modalInner.querySelectorAll(".product-grid-image-container")
      );
      modalContainers.forEach((c) => { c.style.opacity = "1"; });
    } else {
      modal.style.opacity = "0";
      modal.style.display = "flex";
      void modal.offsetHeight;
      modal.style.transition = "opacity 0.3s ease-out";
      modal.style.opacity = "1";
      setTimeout(() => { modal.style.transition = ""; }, 300);

      const modalContainers = Array.from(
        modalInner.querySelectorAll(".product-grid-image-container")
      );
      requestAnimationFrame(() => {
        modalContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });
    }

    if (pushHistory) {
      const params = new URLSearchParams(window.location.search);
      params.set("collection", tertiaryHandle);
      history.pushState(
        { collectionModal: true, handle: tertiaryHandle, title: tertiaryTitle },
        "",
        window.location.pathname + "?" + params.toString()
      );
    }
  }

  function closeModal(updateHistory = true) {
    if (isAnimating) return;
    isAnimating = true;
    ++modalGeneration;

    // Report home with the atTop of the scroll about to be RESTORED (not the
    // modal's own live scroll) — the chrome must snap to the home state now,
    // while the grid fade runs, exactly as the old exitModal at-top branch did.
    window.HeaderState.set({
      view: "home",
      atTop: (window.__indexHomeScrollY || 0) <= 35,
    });

    if (updateHistory) {
      const params = new URLSearchParams(window.location.search);
      params.delete("collection");
      const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      history.pushState({}, "", newUrl);
    }

    const homeContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    // Instant reset for popstate navigation — let the browser own the visual transition.
    if (!updateHistory) {
      modal.style.transition = "none";
      modal.style.opacity = "0";
      modal.style.display = "none";
      hideModalTitle();
      const viewAllCloseBtn = document.getElementById("viewall-close-btn");
      if (viewAllCloseBtn) viewAllCloseBtn.style.display = "none";
      // Restore the index sections to flow, THEN restore the saved home scroll,
      // THEN recompute header opacity (which reads scrollY + section offsetHeight).
      showSections();
      window.scrollTo(0, window.__indexHomeScrollY || 0);
      restoreHeaderOpacity();
      homeContainers.forEach((c) => {
        c.style.transition = "none";
        c.style.opacity = "";
      });
      modalInner.innerHTML = "";
      isAnimating = false;
      return;
    }

    // Animated close for user-triggered dismissals.
    const modalContainers = Array.from(
      modalInner.querySelectorAll(".product-grid-image-container")
    );
    modalContainers.forEach((c) => {
      c.style.transition = "opacity 0.10s ease-out";
      c.style.opacity = "0";
    });

    homeContainers.forEach((c) => {
      c.style.transition = "none";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      modal.style.transition = "none";
      modal.style.display = "none";
      hideModalTitle();

      const viewAllCloseBtn = document.getElementById("viewall-close-btn");
      if (viewAllCloseBtn) viewAllCloseBtn.style.display = "none";

      // Restore sections to flow and the saved home scroll before recomputing
      // header opacity (it reads scrollY + section offsetHeight).
      showSections();
      window.scrollTo(0, window.__indexHomeScrollY || 0);

      requestAnimationFrame(restoreHeaderOpacity);

      requestAnimationFrame(() => {
        homeContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });

      isAnimating = false;
      const closeGeneration = modalGeneration;

      setTimeout(() => {
        if (modalGeneration !== closeGeneration) return;
        if (modal.style.display === "flex") return;
        homeContainers.forEach((c) => {
          c.style.transition = "";
          c.style.opacity = "";
        });
        modalInner.innerHTML = "";
      }, 950);
    }, 100);
  }

  // --- Click handler ---
  document.addEventListener("click", function (e) {
    const link = e.target.closest(".product-grid-image-container a");
    if (!link) return;

    // Collection swap is open: any product click swaps to that PDP in-flow.
    if (modal.style.display === "flex") {
      const productUrl = link.dataset.productUrl || link.getAttribute("href");
      if (productUrl) {
        e.preventDefault();
        openProductSwap(productUrl);
      }
      return;
    }

    const tertiaryHandles = link.dataset.tertiaryHandles || "";
    const tertiaryTitles = link.dataset.tertiaryTitles || "";

    // No tertiary collection (only home+all): swap to the PDP in-flow.
    if (!tertiaryHandles) {
      const productUrl = link.dataset.productUrl || link.getAttribute("href");
      if (productUrl) {
        e.preventDefault();
        openProductSwap(productUrl);
      }
      return;
    }
    if (isAnimating) return;

    e.preventDefault();

    // Resolve the name filter match up front (needed by doOpen below)
    const productHandles = tertiaryHandles.split(",").map((h) => h.trim());
    const nameMenuItems = document.querySelectorAll("[data-menu-type='name']");
    let matchedNameItem = null;

    for (const item of nameMenuItems) {
      const url = item.getAttribute("data-menu-url") || "";
      const allMatch = url.match(/\/all\/([^/?]+)/);
      const colMatch = url.match(/\/collections\/([^/?]+)/);
      const handle = allMatch ? allMatch[1].toLowerCase() : (colMatch ? colMatch[1].toLowerCase() : null);
      if (handle && productHandles.indexOf(handle) !== -1) {
        matchedNameItem = item;
        break;
      }
    }

    // Opens the collection modal (runs after any logo transition completes)
    function doOpen() {
      if (matchedNameItem) {
        const nameDropdown = document.querySelector("[data-filter='name']");
        if (nameDropdown) {
          nameDropdown.value = matchedNameItem.getAttribute("data-menu-item");
          nameDropdown.dispatchEvent(new Event("change"));
          // Override title with the actual collection title from the product
          const firstTitle = tertiaryTitles.split(",")[0].trim() || tertiaryHandles.split(",")[0].trim();
          const modalTitleEl = document.getElementById("collection-modal-title");
          if (modalTitleEl) modalTitleEl.textContent = firstTitle;
          return;
        }
      }
      const firstHandle = tertiaryHandles.split(",")[0].trim();
      const firstTitle = tertiaryTitles.split(",")[0].trim() || firstHandle;
      const label = resolveCollectionLabel(firstHandle);
      window.HeaderState.setNameDisplay(label);
      animateAndOpen(firstHandle, label);
    }

    // Report the view first — the nav snaps logo→bar instantly — then open.
    // (openModal re-pings the same view; idempotent.)
    window.HeaderState.set({ view: "collection" });
    doOpen();
  });

  // --- Close on any click that isn't on a link ---
  // DISABLED (for now): clicking empty negative space on the GRID/body no longer
  // closes the open modal. Re-enable by uncommenting.
  // modal.addEventListener("click", function (e) {
  //   if (e.target.closest(".footer-wrapper")) return;
  //   if (!e.target.closest("a") && !isAnimating) closeModal();
  // });

  // --- Close modal when clicking non-interactive areas of the header or menu ---
  // KEPT: negative-space clicks on the nav BAR (header/menu) still close — only
  // the grid handler above was disabled.
  [document.getElementById("menu"), fixedHeader].forEach((el) => {
    if (!el) return;
    el.addEventListener("click", function (e) {
      if (modal.style.display !== "flex") return;
      if (!e.target.closest("a, button, [onclick]") && !isAnimating) closeModal();
    });
  });

  // --- Close on Escape ---
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape" || modal.style.display !== "flex" || isAnimating) return;
    // If the PDP modal is open ON TOP of this collection modal, Escape belongs to
    // the PDP — its own handler history.back()s, which restores THIS collection
    // underneath. Without this bail both Escape handlers fire on one keypress:
    // the collection is still display:block (only faded to opacity 0 beneath the
    // PDP), so closeModal() here tears it down too and Escape skips straight to
    // the home grid instead of returning to the previous (collection) grid. The
    // PDP overlay's display stays "block" until its 100ms teardown timer, so it
    // reads as open throughout this synchronous keydown regardless of handler order.
    var pdpOverlay = document.getElementById(PRODUCT_MODAL.overlayId);
    if (pdpOverlay && pdpOverlay.style.display !== "none") return;
    closeModal();
  });

  // --- Handle browser back/forward ---
  window.addEventListener("popstate", function (e) {
    // Filter / VIEW ALL state changes are handled by script-index-filters.js
    if (e.state && (e.state.filterModal || e.state.viewAll)) return;
    // PDP swap (and in-PDP family swap) entries are owned by the top-level handler
    // (open/close). The PDP open hid this collection (display:none); the PDP close
    // restores it. Don't touch the collection here, or this handler would tear it
    // down (the else-branch closeModal) the moment we step ONTO a PDP entry.
    if (e.state && (e.state.pdpSwap || e.state.pdpFamily)) return;

    isAnimating = false;
    ++modalGeneration;

    if (e.state && e.state.collectionModal) {
      // Already mounted, OR a PDP swap is mid-close over it (closeProductSwap will
      // restore this collection itself) → skip the rebuild. buildModalGrid would
      // wipe modalInner and re-clone, resetting scroll to 0 — the grid jumping.
      if (modal.style.display === "flex" || pdpSwapClosing) return;
      hideSections();
      openModal(e.state.handle, e.state.title || e.state.handle, { instant: true, pushHistory: false });
    } else if (modal.style.display === "flex" && !pdpSwapClosing) {
      closeModal(false);
    }
  });

  // --- Resolve a display label for a collection handle ---
  // Priority: NAME menu link title → collection backup_title metafield →
  // collection title (via data-tertiary-titles on a matching product) → raw handle.
  function resolveCollectionLabel(handle) {
    const lower = handle.toLowerCase();

    const menuItems = document.querySelectorAll("[data-menu-type='name']");
    for (const item of menuItems) {
      const url = item.getAttribute("data-menu-url") || "";
      const m = url.match(/\/all\/([^/?]+)/) || url.match(/\/collections\/([^/?]+)/);
      if (m && m[1].toLowerCase() === lower) {
        return item.getAttribute("data-menu-item");
      }
    }

    const meta = document.querySelector("#collection-meta [data-col-handle='" + handle + "']");
    if (meta) {
      const backup = meta.getAttribute("data-col-backup-title");
      if (backup) return backup;
    }

    const firstMatch = document.querySelector(
      "#product-grid-main .product-grid-image-container[data-tertiary-handles*='" + handle + "']"
    );
    if (firstMatch) {
      const handles = (firstMatch.dataset.tertiaryHandles || "").split(",").map((h) => h.trim());
      const titles = (firstMatch.dataset.tertiaryTitles || "").split(",").map((t) => t.trim());
      const idx = handles.indexOf(handle);
      if (idx !== -1 && titles[idx]) return titles[idx];
    }

    return handle;
  }

  // --- Auto-open from URL ---
  const initialParams = new URLSearchParams(window.location.search);
  const initialHandle = initialParams.get("collection");
  const hasFilterParam = initialParams.get("name") || initialParams.get("category") || initialParams.get("finish");
  // Only handle ?collection= here; filter params are handled by script-index-filters.js
  if (initialHandle && !hasFilterParam) {
    const label = resolveCollectionLabel(initialHandle);
    // PRESERVE the deep-link URL (keep ?collection=h in the address bar) so the
    // view is reloadable and shareable. We do NOT clean it to "/". Instead we
    // ATTACH the matching collectionModal state to this initial history entry
    // (a fresh page load lands here with null state). That state is what the
    // popstate handler reads to RE-open the collection if the user opens a PDP
    // from it and then steps back onto this entry — without it, back would close
    // the modal but leave ?collection=h stale in the bar. pushHistory:false on
    // openModal below avoids stacking a duplicate entry on top of this one.
    history.replaceState(
      { collectionModal: true, handle: initialHandle, title: label },
      "",
      window.location.pathname + window.location.search
    );
    // Set the header to its collection state FIRST (hide the logo, show the
    // filter bar) — the deep-link path skips the product-click
    // enterModal→revealFilters that normally does this, which is why the logo
    // flashed before the name. Then build the modal, name it, and only then drop
    // the early-hide style so nothing in between is visible.
    window.HeaderState.set({ view: "collection" });
    // instant:false → the collection grid FADES in on a deep-link reload, exactly
    // like the home grid's fadeInHome. (popstate restores from a PDP still use
    // instant:true so stepping back doesn't re-animate.)
    openModal(initialHandle, label, { instant: false, pushHistory: false });
    window.HeaderState.setNameDisplay(label);
    const earlyStyle = document.getElementById("cl-deeplink-hide");
    if (earlyStyle) earlyStyle.remove();
  }

  window.closeCollectionModal = closeModal;
  // Exposed so the top-level PDP body-swap (pmSwapHideUnderlay / pmSwapShowUnderlay)
  // can hide/restore the index sections when opening over the bare home grid —
  // the same helpers the collection modal uses.
  window.__hideIndexSections = hideSections;
  window.__showIndexSections = showSections;
});
